<template>
  <div v-if="!prdEnv()" class="ai-search-page">

    <!-- ===== PAGE HEADER ===== -->
    <div class="page-header">
      <div class="d-flex align-items-start justify-content-between">
        <div class="d-flex align-items-start">
          <div class="header-icon-wrap mr-3 mt-1">
            <i class="fa fa-search-plus"></i>
          </div>
          <div>
            <div class="caption-label mb-0">{{ function_name }}</div>
            <div class="header-subtitle">Powered by Getac On-Premise AI · Genova </div>

            <!-- Report Portal Links (Project Owner only) -->
            <div v-if="reportAccess() || (userRoles && _.indexOf(userRoles, 'Admin') > -1)"
                 class="report-portal-links mt-1">
              <span class="report-portal-label">
                <i class="fa fa-bar-chart mr-1"></i>Reports:
              </span>
              <a :href="`../custom-level-report`" target="_blank" class="report-portal-btn">
                <i class="fa fa-line-chart mr-1"></i>Custom Level Report
              </a>
              <span class="report-portal-divider">|</span>
              <a :href="`../custom-desc-dashboard`" target="_blank" class="report-portal-btn">
                <i class="fa fa-tachometer mr-1"></i>Custom Desc Dashboard
              </a>
            </div>

          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="warning" class="ai-badge mr-2 mt-1" v-if="searchMode !=='keyword'">
            <i class="fa fa-bolt mr-1"></i>AI Enhanced
          </b-badge>
          <!--
          <b-badge :variant="searchMode === 'semantic' ? 'primary' : 'secondary'" class="mode-badge">
            {{ searchMode === 'semantic' ? 'Semantic Mode' : 'Keyword Mode' }}
          </b-badge>
          -->
          <b-badge :variant="searchMode === 'semantic' ? 'primary' : 'secondary'" class="mode-badge">
            {{ searchMode !== 'keyword' ? 'Hybrid Mode (keyword+semantic)' : 'Keyword Mode' }}
          </b-badge>
        </div>
      </div>
    </div>

    <!-- ===== SEARCH PANEL ===== -->
    <div class="search-panel mt-3">

      <!-- SEARCH MODE TABS -->
      <div class="search-mode-tabs">
        <!--
                <button class="mode-tab"
                        :class="{ active: searchMode === 'semantic' }"
                        @click="searchMode = 'semantic'">
                  <i class="fa fa-magic mr-1"></i> AI Semantic Search
                  <span class="tab-desc">Describe your issue in natural language</span>
                </button>
        -->

        <button class="mode-tab"
                :class="{ active: searchMode === 'keyword' }"
                @click="searchModeChange('keyword')">
          <i class="fa fa-filter mr-1"></i> Page View & Traditional Search
          <span class="tab-desc">Legacy feature about to EOL: Filter-based search with page-by-page browsing.</span>
        </button>
        <button class="mode-tab"
                :class="{ active: searchMode === 'hybrid' }"
                @click="searchModeChange('hybrid')">
          <i class="fa fa-magic mr-1"></i> AI Hybrid Search
          <span style="font-size:0.68rem; color:#f0a04b; font-weight:500; margin-left:4px; letter-spacing:0.02em;">✨ Recommended</span>
          <span class="tab-desc">Smarter results — combines semantic understanding, filters & AI-powered ranking</span>
        </button>
      </div>

      <!-- SEARCH BODY -->
      <div class="search-body p-3">

        <!-- COMMON CONDITION FILTERS (shown in keyword & hybrid modes) -->
        <div v-if="searchMode !== 'semantic'" class="condition-filters mb-3">
          <div class="filter-group-label mb-2">
            <i class="fa fa-sliders mr-1"></i> Filter Conditions
          </div>
          <div class="row">
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Model / Device</label>
              <gtc-multi-select v-model="model"
                                key-field="value"
                                text-field="text"
                                :options="model_options"
                                select-class="w-100"
                                class="w-100"
                                placeholder="All Models"></gtc-multi-select>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">GRO Region</label>
              <b-form-select v-model="GRO" :options="GRO_options" class="gtc-select"></b-form-select>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Ticket Type</label>
              <!--<b-form-select v-model="ticket_type"
              :options="ticket_type_options_with_all"
              class="gtc-select"></b-form-select>-->
              <gtc-multi-select v-model="ticket_type"
                                key-field="value"
                                text-field="text"
                                :options="ticket_type_options_with_all"
                                select-class="w-100"
                                class="w-100"></gtc-multi-select>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Status</label>
              <gtc-multi-select v-model="ticket_status"
                                key-field="value"
                                text-field="text"
                                :options="ticket_status_options"
                                select-class="w-100"
                                class="w-100"></gtc-multi-select>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">GRO Owner (L1):</label>
              <b-form-select v-model="GRO_owner" :options="GROOwnerList" label="GRO Owner:"></b-form-select>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">GTC Owner (L2):</label>
              <b-form-select v-model="GTC_owner" :options="GTCOwnerList" label="GTC Owner:"></b-form-select>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Escalation Date From</label>
              <b-form-input v-model="date_from" type="date" class="gtc-input"></b-form-input>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Escalation Date To</label>
              <b-form-input v-model="date_to" type="date" class="gtc-input"></b-form-input>
            </div>
          </div>
          <div class="row mt-1">
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Customer</label>
              <b-form-input v-model="customer"
                            placeholder=""
                            label="Customer" @keydown.enter="search()"
                            trim>
              </b-form-input>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Keyword Category</label>
              <b-form-select v-model="keyword_category"
                             key-field="value"
                             text-field="text"
                             :options="KeyWordCategoryList"
                             class="gtc-select">

              </b-form-select>
            </div>
            <div class="col-md-3 col-sm-6 mb-2">
              <label class="sub-label">Keyword</label>
              <b-form-input v-model="keywords" placeholder="FTIE ID, PN, SN, Subject..." class="gtc-input"></b-form-input>
            </div>
          </div>
        </div>

        <!-- SEMANTIC / DESCRIPTION INPUT (shown in semantic & hybrid modes) -->
        <div v-if="searchMode !== 'keyword'" class="semantic-input-area mb-3">
          <div class="d-flex align-items-center mb-1">
            <label class="sub-label mb-0 mr-2">
              <i class="fa fa-comment-alt mr-1" style="color:#f77530"></i>
              Describe the Engineering Service / FTIE Issue
            </label>
            <span class="ai-hint-badge"><i class="fa fa-robot mr-1"></i>AI will find similar tickets &amp; summarize</span>
          </div>
          <div class="semantic-textarea-wrap">
            <b-form-textarea v-model="es_context"
                             :placeholder="semanticPlaceholder"
                             rows="3"
                             max-rows="6"
                             class="semantic-textarea"
                             @keydown.ctrl.enter="handleSearch"></b-form-textarea>
            <div class="textarea-footer d-flex justify-content-between align-items-center px-2 py-1">
              <span class="char-count" :class="{ 'text-warning': es_context && es_context.length > 300, 'text-danger': es_context && es_context.length > 350 }">
                {{ es_context ? es_context.length : 0 }} / 350
              </span>
              <span class="text-muted" style="font-size:0.72rem">Ctrl+Enter to search</span>
            </div>
          </div>
        </div>

        <!-- ACTION BUTTONS -->
        <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
          <b-button variant="primary"
                    class="btn-search-main"
                    :disabled="searching"
                    @click="handleSearch">
            <span v-if="!searching">
              <i class="fa fa-search mr-1"></i> Search
            </span>
            <span v-else>
              <i class="fa fa-circle-o-notch fa-spin mr-1"></i> Searching...
            </span>
          </b-button>
          <b-button v-if="searchMode === 'keyword' && (userSites && userSites.includes('Getac'))"
                    variant="primary"
                    class="btn-search-main"
                    :disabled="searching"
                    @click="getKpiReport">
            <span v-if="!searching">
              <i class="fa fa-search mr-1"></i> KPI Report
            </span>
            <span v-else>
              <i class="fa fa-circle-o-notch fa-spin mr-1"></i> Loading Report...
            </span>
          </b-button>

          <b-button variant="outline-secondary" size="sm" @click="resetFilterCondition">
            <i class="fa fa-undo mr-1"></i> Reset
          </b-button>
          <div class="ml-auto d-flex align-items-center" style="gap:0.5rem"
               v-if="searchMode !== 'keyword'">
            <div v-if="!es_context || es_context.trim().length === 0" class="ai-overview-prereq-hint mb-1">
              <span>Enter an ES description above and run Search to unlock AI Overview</span>
            </div>

            <div v-if="!es_context || es_context.trim().length === 0" class="ai-overview-prereq-hint mb-1">
              <i class="fa fa-keyboard-o hint-step-icon"></i>
              <span class="hint-step-text">Describe issue</span>
              <i class="fa fa-long-arrow-right hint-arrow"></i>
              <i class="fa fa-search hint-step-icon"></i>
              <span class="hint-step-text">Search</span>
              <i class="fa fa-long-arrow-right hint-arrow"></i>
              <i class="fa fa-magic hint-step-icon" style="color:#f77530"></i>
            </div>
            <div class="d-flex align-items-center" style="gap:0.75rem">
              <!-- AI Overview toggle: locked until es_context has input -->
              <div class="d-flex align-items-center" style="gap:0.3rem">
                <i v-if="!es_context || es_context.trim().length === 0"
                   class="fa fa-lock ai-overview-lock-icon"
                   v-b-tooltip.hover title="Enter an ES description above and run Search to unlock AI Overview"></i>
                <i v-else
                   class="fa fa-unlock-alt ai-overview-lock-icon unlocked"
                   v-b-tooltip.hover title="AI Overview is ready — it will generate after Search"></i>
                <b-form-checkbox v-model="includeAIOverview"
                                 :disabled="!es_context || es_context.trim().length === 0"
                                 switch size="sm">
                  <span :class="['ai-overview-label', (!es_context || es_context.trim().length === 0) ? 'label-locked' : 'label-unlocked']">AI Overview</span>
                </b-form-checkbox>
              </div>
              <!--
                <b-form-checkbox v-model="includeCopilotAgent"
                   v-b-tooltip.hover title="Copilot Agent is currently under testing and will be available soon"
                   @change.prevent="includeCopilotAgent = false"
                   :disabled="true" switch size="sm">
                 <span style="font-size:0.8rem; color:#555">Copilot Agent</span>
              </b-form-checkbox>
              -->
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ===== AI OVERVIEW SECTION ===== -->
    <transition name="fade-slide">
      <div v-if="showAIOverviewCard" class="ai-overview-card mt-3">
        <div class="ai-overview-header">
          <div class="d-flex align-items-center">
            <div class="ai-spark-icon mr-2">
              <i class="fa fa-star" style="color:#f77530"></i>
            </div>
            <div>
              <div class="ai-overview-title">AI Overview</div>
              <div class="ai-overview-sub">Generated from {{ rows }} matched Engineering Service records</div>
            </div>
          </div>
          <div class="d-flex align-items-center" style="gap:0.5rem">
            <b-badge variant="light" class="source-badge">
              <i class="fa fa-database mr-1"></i>{{ rows }} sources
            </b-badge>
            <b-button variant="link" size="sm" class="p-0" @click="dismissAIOverviewCard()">
              <i class="fa fa-times text-muted"></i>
            </b-button>
          </div>
        </div>

        <div class="ai-overview-body">
          <div v-if="decomposition && decomposition.search_mode !== 'semantic_only'"
               class="decomp-chips mt-1">
            <b-badge variant="info" class="mr-1">
              Mode: {{ decomposition.search_mode }}
            </b-badge>
            <b-badge v-for="(v, k) in decomposition.structured_filters"
                     :key="k" variant="light" class="mr-1">
              <i class="fa fa-filter mr-1"></i>{{ k }}: {{ v }}
            </b-badge>
          </div>

          <!-- Animated typing indicator while loading -->
          <div v-if="searching" class="typing-indicator">
            <span></span><span></span><span></span>
            <span class="ml-2" style="font-size:0.82rem; color:#888">AI is analyzing matched tickets...</span>
          </div>

          <!-- Copilot Agent Result -->
          <div v-if="copilotStudioAgentResponse && includeCopilotAgent" class="overview-block mb-2">
            <div class="overview-block-label"><i class="fa fa-robot mr-1"></i>Copilot Agent</div>
            <div class="overview-content" v-html="safeHtml(paperOutputAgent || copilotStudioAgentResponse)"></div>
          </div>


          <!-- Semantic Similarity Result -->
          <div v-if="similarityResult && includeAIOverview" class="overview-block">
            <!-- Clickable label header -->
            <div class="overview-block-label" style="cursor:pointer; display:flex; justify-content:space-between; align-items:center;" @click="aiOverviewExpanded = !aiOverviewExpanded">
              <span>
                <i class="fa fa-brain mr-1"></i>Semantic Match Summary
                <span v-if="!aiOverviewExpanded" style="font-size:0.68rem; font-weight:400; color:#bbb; margin-left:6px;">({{ parsedResults.length }} matches)</span>
              </span>
              <i :class="['fa', aiOverviewExpanded ? 'fa-chevron-up' : 'fa-chevron-down']" style="font-size:0.75rem; color:#bbb;"></i>
            </div>

            <!-- Collapsible cards -->
            <transition name="fade-slide">
              <div v-show="aiOverviewExpanded" class="similarity-cards">
                <div v-for="(item, index) in visibleSemanticResults"
                     :key="item.ftie_id"
                     class="similarity-card">
                  <!-- Card Header -->
                  <div class="card-header-row">
                    <span class="card-rank">#{{ index + 1 }}</span>
                    <span class="card-ftie-id">
                      <i class="fa fa-ticket mr-1"></i>
                      <a :href="`${frontEndUrlToUse}ftie/ftie-page/${item.ftie_id}`" target="_blank">
                        FTIE#{{ item.ftie_id }}
                      </a>
                    </span>
                    <span class="card-score" :class="scoreClass(item.score)">
                      {{ item.score }}<i class="fa fa-percent mr-1"></i>
                    </span>
                  </div>

                  <!-- Semantic Summary -->
                  <div class="card-section">
                    <div class="card-section-label">
                      <i class="fa fa-lightbulb-o mr-1"></i>AI Summary
                    </div>
                    <div class="card-section-body summary-text">{{ item.semantic_summary }}</div>
                  </div>

                  <!-- Service Request -->

                  <div class="card-section">
                    <div class="card-section-label">
                      <i class="fa fa-inbox mr-1"></i>Original Service Request
                    </div>
                    <div class="card-section-body">
                      <span>
                        {{
isServiceRequestExpanded(item.ftie_id)
                              ? item.service_request
                              : truncateText(item.service_request, serviceRequestPreviewLength)
                        }}
                      </span>
                      <button v-if="item.service_request && item.service_request.length > serviceRequestPreviewLength"
                              type="button"
                              class="btn-read-more btn-read-more-inline ml-1"
                              @click="toggleServiceRequest(item.ftie_id)">
                        <i :class="['fa', isServiceRequestExpanded(item.ftie_id) ? 'fa-chevron-up' : 'fa-chevron-down', 'mr-1']"></i>
                        <span v-if="!isServiceRequestExpanded(item.ftie_id)">Show more</span>
                        <span v-else>Show less</span>
                      </button>
                    </div>
                  </div>


                </div>

                <!-- Read more / Show less -->
                <div v-if="parsedResults.length > semanticPreviewCount" class="read-more-wrap">
                  <button type="button" class="btn-read-more" @click="semanticShowAll = !semanticShowAll">
                    <i :class="['fa', semanticShowAll ? 'fa-chevron-up' : 'fa-chevron-down', 'mr-1']"></i>
                    <span v-if="!semanticShowAll">Read more ({{ parsedResults.length - semanticPreviewCount }} more)</span>
                    <span v-else>Show less</span>
                  </button>
                </div>

                <!-- Key References pills — shown inside Semantic Match Summary -->
                <div v-if="searchedData && searchedData.length" class="ai-overview-sources mt-2"
                     style="border-radius:6px; border-top:none; border:1px solid #f0c8a0;">
                  <span class="source-pill-label">Key References / TOP 5:</span>
                  <span v-for="(item, idx) in searchedData.slice(0, 5)"
                        :key="idx"
                        class="source-pill">
                    <a :href="`${frontEndUrlToUse}ftie/ftie-page/${item.ftie_id}`" target="_blank">
                      FTIE#{{ item.ftie_id }}
                    </a>
                    <span v-if="item.similarity_score" class="pill-score">{{ item.similarity_score }}</span>
                  </span>
                </div>
              </div>
            </transition>

          </div>


          <!-- ===== AI HIGHLIGHT (opt-in, click-to-generate) ===== -->
          <div v-if="includeAIOverview" class="overview-block ai-highlight-block mt-2">

            <!-- 收合 / 標題列 -->
            <div class="overview-block-label ai-highlight-label"
                 style="cursor:pointer; display:flex; justify-content:space-between; align-items:center;"
                 @click="aiHighlightLoaded ? toggleAIHighlight() : enterSelectionMode()">
              <span>
                <i class="fa fa-bolt mr-1" style="color:#f0a04b"></i>AI Highlight
                <span class="ai-highlight-sub">
                  Tickets Overall Highlight Summary<span v-if="aiHighlightLoaded"> · TOP {{ highlightTargets.length }}</span>
                </span>
                <b-badge variant="light" class="ml-1 ai-highlight-mock-badge">Preview</b-badge>
              </span>
              <span>
                <!-- Not yet generated, not in selection mode -->
                <span v-if="!aiHighlightLoaded && !selectionMode" class="ai-highlight-cta mr-2">
                  <i class="fa fa-magic mr-1"></i>Click to select &amp; generate
                </span>
                <!-- Not yet generated, selection mode active (first time) -->
                <span v-else-if="!aiHighlightLoaded && selectionMode" class="ai-highlight-cta mr-2" style="color:#c17f00;">
                  <i class="fa fa-check-square-o mr-1"></i>Selecting below…
                </span>
                <!-- Already generated, re-selection in progress -->
                <span v-else-if="aiHighlightLoaded && selectionMode" class="ai-highlight-cta mr-2" style="color:#c17f00;">
                  <i class="fa fa-refresh mr-1"></i>Re-selecting below…
                </span>
                <!-- Already generated, collapsed -->
                <span v-else-if="aiHighlightLoaded && !showAIHighlight" class="ai-highlight-cta mr-2">Click to view</span>
                <i v-if="aiHighlightLoading" class="fa fa-circle-o-notch fa-spin" style="font-size:0.75rem;color:#c17f00;"></i>
                <i v-else :class="['fa', showAIHighlight && aiHighlightLoaded ? 'fa-chevron-up' : 'fa-chevron-down']"
                   style="font-size:0.75rem; color:#bbb;"></i>
              </span>
            </div>

            <!-- 內容 - structured highlight sections -->
            <transition name="fade-slide">
              <div v-show="showAIHighlight && aiHighlightLoaded" class="ai-highlight-text-wrap">
                <div v-if="parsedHighlightSections.length" class="ai-highlight-sections">
                  <div v-for="section in parsedHighlightSections"
                       :key="section.title"
                       class="hl-section-card"
                       :class="`hl-section-${section.meta.key}`">
                    <div class="hl-section-header">
                      <i :class="['fa', section.meta.icon, 'mr-1']"></i>
                      <span class="hl-section-title">{{ section.title }}</span>
                    </div>
                    <div class="hl-section-body">
                      <div v-for="(line, lineIdx) in section.lines" :key="lineIdx">
                        <p v-if="line.type === 'text'" class="hl-line-text">{{ line.text }}</p>
                        <p v-else-if="line.type === 'sub-header'" class="hl-line-subheader">{{ line.text }}</p>
                        <div v-else-if="line.type === 'bullet'" class="hl-line-bullet">{{ line.text }}</div>
                        <div v-else-if="line.type === 'sub-bullet'" class="hl-line-sub-bullet">{{ line.text }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <pre v-else class="ai-highlight-text">{{ aiHighlightText }}</pre>

                <!-- Highlight Source pills — shows which tickets were used for this AI Highlight -->
                <div class="ai-overview-sources mt-2"
                     style="border-radius:6px; border-top:none; border:1px solid #f0c8a0; background:rgba(240,160,75,0.06);">
                  <span class="source-pill-label">
                    <i class="fa fa-bolt mr-1" style="color:#f0a04b"></i>Highlight Source · TOP {{ highlightTargets.length }}:
                  </span>
                  <span v-for="(item, idx) in highlightTargets"
                        :key="idx"
                        class="source-pill">
                    <a :href="`${frontEndUrlToUse}ftie/ftie-page/${item.ftie_id}`" target="_blank">
                      FTIE#{{ item.ftie_id }}
                    </a>
                    <span v-if="item.similarity_score" class="pill-score">{{ item.similarity_score }}</span>
                  </span>
                </div>
              </div>
            </transition>
          </div>
          <!-- ===== END AI HIGHLIGHT ===== -->
          <!--=  -->

        </div>

      </div>
    </transition>

    <!-- ===== AI ADVANCED TOOLS PANEL [1] ===== -->
    <div v-if="!aiToolsComingSoon && already_first_search_flag && searchedData !== null && searchedData.length > 0 && !prdEnv() && (userRoles && _.indexOf(userRoles, 'Admin') > -1)" class="ai-tools-panel mt-3">
      <div class="ai-tools-header">
        <i class="fa fa-magic mr-2" style="color:#f77530"></i>
        <span class="ai-tools-title">AI Advanced Tools</span>
        <span class="ai-tools-subtitle ml-2">— Leverage AI to accelerate your Engineering Service workflow</span>
        <b-badge v-if="aiToolsComingSoon" variant="warning" class="ml-2" style="font-size:0.7rem; vertical-align:middle">
          🚧 Coming Soon — Under Development
        </b-badge>
      </div>
      <div class="ai-tools-grid mt-2">

        <!-- Tool 1: AI Draft Proposal -->
        <div class="ai-tool-card" :class="{ 'tool-active': activeTool === 'proposal', 'tool-disabled': aiToolsComingSoon }">
          <div class="tool-icon-wrap" style="background:rgba(247,117,48,0.1)">
            <i class="fa fa-file-text" :style="{ color: aiToolsComingSoon ? '#bbb' : '#f77530' }"></i>
          </div>
          <div class="tool-content">
            <div class="tool-name" :style="{ color: aiToolsComingSoon ? '#aaa' : '' }">AI Draft Proposal</div>
            <div class="tool-desc">Auto-generate a structured ES proposal based on similar FTIE cases</div>
          </div>
          <div class="d-flex flex-column align-items-end" style="gap:0.3rem">
            <b-badge v-if="aiToolsComingSoon" variant="secondary" style="font-size:0.65rem">Coming Soon</b-badge>
            <b-button size="sm"
                      :variant="activeTool === 'proposal' ? 'warning' : 'outline-warning'"
                      :disabled="!searchedData || !searchedData.length || aiToolsComingSoon"
                      @click="toggleTool('proposal')">
              <i class="fa fa-rocket mr-1"></i>{{ activeTool === 'proposal' ? 'Close' : 'Generate' }}
            </b-button>
          </div>
        </div>

        <!-- Tool 2: NRE Cost Insight -->
        <div class="ai-tool-card" :class="{ 'tool-active': activeTool === 'nre', 'tool-disabled': aiToolsComingSoon }">
          <div class="tool-icon-wrap" style="background:rgba(0,123,255,0.08)">
            <i class="fa fa-usd" :style="{ color: aiToolsComingSoon ? '#bbb' : '#007bff' }"></i>
          </div>
          <div class="tool-content">
            <div class="tool-name" :style="{ color: aiToolsComingSoon ? '#aaa' : '' }">ETO NRE Insight</div>
            <div class="tool-desc">AI-estimated NRE cost and price range based on similar customization cases</div>
          </div>
          <div class="d-flex flex-column align-items-end" style="gap:0.3rem">
            <b-badge v-if="aiToolsComingSoon" variant="secondary" style="font-size:0.65rem">Coming Soon</b-badge>
            <b-button size="sm"
                      :variant="activeTool === 'nre' ? 'primary' : 'outline-primary'"
                      :disabled="!searchedData || !searchedData.length || aiToolsComingSoon"
                      @click="toggleTool('nre')">
              <i class="fa fa-bar-chart mr-1"></i>{{ activeTool === 'nre' ? 'Close' : 'Analyze' }}
            </b-button>
          </div>
        </div>

        <!-- Tool 3: TCO / TVO Analysis -->
        <div class="ai-tool-card" :class="{ 'tool-active': activeTool === 'tco', 'tool-disabled': aiToolsComingSoon }">
          <div class="tool-icon-wrap" style="background:rgba(40,167,69,0.08)">
            <i class="fa fa-balance-scale" :style="{ color: aiToolsComingSoon ? '#bbb' : '#28a745' }"></i>
          </div>
          <div class="tool-content">
            <div class="tool-name" :style="{ color: aiToolsComingSoon ? '#aaa' : '' }">TCO / TVO Analysis</div>
            <div class="tool-desc">Draft Total Cost of Ownership &amp; Total Value of Ownership for customization requests</div>
          </div>
          <div class="d-flex flex-column align-items-end" style="gap:0.3rem">
            <b-badge v-if="aiToolsComingSoon" variant="secondary" style="font-size:0.65rem">Coming Soon</b-badge>
            <b-button size="sm"
                      :variant="activeTool === 'tco' ? 'success' : 'outline-success'"
                      :disabled="!searchedData || !searchedData.length || aiToolsComingSoon"
                      @click="toggleTool('tco')">
              <i class="fa fa-calculator mr-1"></i>{{ activeTool === 'tco' ? 'Close' : 'Calculate' }}
            </b-button>
          </div>
        </div>

        <!-- Tool 4: Parts & Shipment Intelligence -->
        <div class="ai-tool-card" :class="{ 'tool-active': activeTool === 'parts', 'tool-disabled': aiToolsComingSoon }">
          <div class="tool-icon-wrap" style="background:rgba(32,178,170,0.1)">
            <i class="fa fa-archive" :style="{ color: aiToolsComingSoon ? '#bbb' : '#20b2aa' }"></i>
          </div>
          <div class="tool-content">
            <div class="tool-name" :style="{ color: aiToolsComingSoon ? '#aaa' : '' }">Parts &amp; Shipment Intelligence</div>
            <div class="tool-desc">AI cross-references matched cases to surface relevant part numbers, lead times &amp; historical shipment patterns</div>
          </div>
          <div class="d-flex flex-column align-items-end" style="gap:0.3rem">
            <b-badge v-if="aiToolsComingSoon" variant="secondary" style="font-size:0.65rem">Coming Soon</b-badge>
            <b-button size="sm"
                      :variant="activeTool === 'parts' ? 'info' : 'outline-info'"
                      :disabled="!searchedData || !searchedData.length || aiToolsComingSoon"
                      @click="toggleTool('parts')">
              <i class="fa fa-ship mr-1"></i>{{ activeTool === 'parts' ? 'Close' : 'Lookup' }}
            </b-button>
          </div>
        </div>

      </div>
    </div>
    <!-- ===== END OF AI TOOLS PANEL ===== -->
    <!-- ===== SEARCH RESULTS ===== -->
    <div class="mt-3" v-if="already_first_search_flag">
      <div class="results-header d-flex align-items-center justify-content-between mb-2">
        <div class="d-flex align-items-center">
          <div class="h6 font-weight-bold mb-0 mr-2">
            <i class="fa fa-list-ul mr-1" style="color:#f77530"></i>
            {{ rows }} Results Found
          </div>
          <!-- Selection count chip (visible only in selection mode, not in keyword/legacy tab) — click to clear -->
          <b-badge v-if="selectionMode && selectedFtieIds.length > 0 && searchMode !== 'keyword'"
                   variant="warning"
                   class="mr-1"
                   style="font-size:0.72rem; cursor:pointer; user-select:none"
                   v-b-tooltip.hover title="Click to clear selection"
                   @click="selectedFtieIds = []">
            <i class="fa fa-check-square-o mr-1"></i>{{ selectedFtieIds.length }} selected
            <i class="fa fa-times ml-1"></i>
          </b-badge>
          <b-badge v-if="searchMode !== 'keyword'
                   && includeAIOverview !== true
                   && (es_context !== null && es_context !== '')" variant="light" style="font-size:0.72rem">
            Ranked by AI similarity score
          </b-badge>
        </div>
        <div class="d-flex align-items-center" style="gap:0.5rem">
          <span style="font-size:0.8rem; color:#888">
            Showing {{ 1 + (currentPage - 1) * perPage }} – {{ Math.min(searchedData.length + (currentPage - 1) * perPage, rows) }} of {{ rows }}
          </span>
          <select class="form-control form-control-sm" v-model="perPage" v-on:change="perPageChange" style="width:70px">
            <option>10</option>
            <option>20</option>
            <option>50</option>
            <option>100</option>
          </select>
          <!-- AI Highlight button — not available in keyword/legacy mode; hidden while selection mode active. -->
          <b-button v-if="already_first_search_flag && searchedData && searchedData.length > 0 && !selectionMode && searchMode !== 'keyword'"
                    size="sm"
                    :variant="showKeywordAIHighlightCard ? 'warning' : 'outline-warning'"
                    :disabled="aiHighlightLoading"
                    @click="enterSelectionMode()">
            <i v-if="aiHighlightLoading" class="fa fa-circle-o-notch fa-spin mr-1" style="color:#c17f00"></i>
            <i v-else class="fa fa-bolt mr-1" style="color:#f0a04b"></i>
            AI Highlight<span v-if="aiHighlightLoaded" class="ml-1" style="font-size:0.75em; opacity:0.75;">↺</span>
          </b-button>
        </div>
      </div>

      <!-- ===== SELECTION MODE BANNER ===== -->
      <!-- Shown when user has clicked AI Highlight to select tickets (not in keyword/legacy mode) -->
      <transition name="fade-slide">
        <div v-if="selectionMode && searchMode !== 'keyword'"
             class="selection-mode-banner d-flex align-items-center flex-wrap px-3 py-2 mb-2">
          <i class="fa fa-check-square-o mr-2" style="color:#f0a04b; font-size:1rem"></i>
          <span style="font-size:0.85rem; font-weight:600; color:#333;">
            Select tickets for AI Highlight
          </span>
          <span v-if="selectedFtieIds.length > 0"
                :style="{fontSize: '0.8rem' ,marginLeft: '6px' ,color: selectedFtieIds.length >= maxHighlightTickets ? '#d9534f' : '#c17f00',fontWeight: selectedFtieIds.length >= maxHighlightTickets ? '600' : 'normal',}">
            &nbsp;· {{ selectedFtieIds.length }} / {{ maxHighlightTickets }} selected
            <span v-if="selectedFtieIds.length >= maxHighlightTickets">
              &nbsp;— <i class="fa fa-exclamation-triangle mr-1"></i>Maximum reached, no further selection allowed
            </span>
          </span>
          <span v-else style="font-size:0.78rem; color:#888; margin-left:6px;">
            &nbsp;(no selection = use first {{maxHighlightTickets}} results)
          </span>
          <div class="ml-auto d-flex" style="gap:0.5rem">
            <b-button size="sm" variant="warning"
                      :disabled="aiHighlightLoading"
                      @click="confirmAIHighlight()">
              <i v-if="aiHighlightLoading" class="fa fa-circle-o-notch fa-spin mr-1"></i>
              <i v-else class="fa fa-check mr-1"></i>
              Generate<span v-if="selectedFtieIds.length > 0"> ({{ selectedFtieIds.length }})</span><span v-else> (all)</span>
            </b-button>
            <b-button size="sm" variant="outline-secondary"
                      @click="cancelSelectionMode()">
              <i class="fa fa-times mr-1"></i>Cancel
            </b-button>
          </div>
        </div>
      </transition>
      <!-- ===== END SELECTION MODE BANNER ===== -->

      <div v-if="searchMode === 'hybrid'" class="table-responsive" style="min-height: 2.5rem">
        <b-table v-if="searchedData !== []"
                 :fields="fields"
                 :items="searchedData"
                 small
                 hover
                 thead-class="thead-getac text-dark"
                 :busy="searching"
                 style="width: calc(100% - 10px)"
                 :tbody-tr-class="unreadRowClass">
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle mr-2"></b-spinner>
              <strong>Loading results...</strong>
            </div>
          </template>

          <!-- Select-all checkbox in header; indeterminate when partially selected -->
          <template #head(_select)>
            <b-form-checkbox :checked="allCurrentPageSelected"
                             :indeterminate="selectedFtieIds.length > 0 && !allCurrentPageSelected"
                             @change="toggleSelectAll">
            </b-form-checkbox>
          </template>
          <template #cell(_select)="data">
            <b-form-checkbox :value="String(data.item.ftie_id)"
                             v-model="selectedFtieIds">
            </b-form-checkbox>
          </template>

          <!-- AI Score Column -->
          <template #cell(similarity_score)="data">
            <div class="score-cell">
              <div class="score-bar-wrap">
                <div class="score-bar" :style="{ width: (data.item.similarity_score * 1) + '%' }"></div>
              </div>
              <span class="score-label">{{ (data.item.similarity_score * 1).toFixed(0) }}%</span>
            </div>
          </template>

          <!-- FTIE ID Column -->
          <template #cell(ftie_id)="data">
            <div class="d-flex align-items-center">
              <button v-if="data.item.sub_tickets && data.item.sub_tickets.length !== 0"
                      class="btn btn-sm btn-link p-0 mr-1 btn-svg fontawesome-chevron-down"
                      type="button"
                      style="width:1.2rem; height:1.2rem"
                      data-toggle="collapse"
                      aria-expanded="false"
                      @click="data.toggleDetails"
                      v-b-tooltip.hover.rightbottom
                      title="Expand sub-tickets"></button>
              <a class="link font-weight-bold" target="_blank" :href="`${frontEndUrlToUse}ftie/ftie-page/${data.item.ftie_id}`">
                {{ data.item.ftie_id }}
              </a>
            </div>
          </template>

          <!-- SF Opportunity Column -->
          <template #cell(salesforce_opty_id)="data">
            <div v-if="ifShowOptyLink(data.item.site)">
              <gtc-opportunity :opportunityId="data.item.salesforce_opty_id"
                               :opportunityName="data.item.salesforce_opty_name" />
            </div>
            <div v-else>{{ data.item.salesforce_opty_name }}</div>
          </template>

          <!-- NRE Action Column -->
          <template #cell(nre_exist)="data">
            <button class="btn border-radius-0 w-100"
                    v-if="data.item.nre_exist === true"
                    @click="redirectToEtoDashboard(data.item.ftie_id)"
                    v-b-tooltip.hover title="View NRE Dashboard">
              <img src="../../../static/image/icon-NRE_orange.svg" alt="NRE Icon" />
            </button>
          </template>

          <!-- Status Column with badge -->
          <template #cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)" class="status-badge">
              {{ data.item.status }}
            </b-badge>
          </template>

          <!-- Row Details (Sub-tickets) -->
          <template #row-details="data">
            <div class="sub-ticket-detail">
              <b-row class="mb-1"
                     v-for="(ticket, index) in data.item.sub_tickets"
                     :key="index">
                <b-col class="col-2">
                  <i class="fa fa-level-up-alt fa-rotate-90 mr-1 text-muted" style="font-size:0.7rem"></i>
                  <strong>sub: FTIE#</strong>
                  <a v-if="(userSites.includes('Getac') || userSites.includes('GTK')) || login_user_name === data.item.gtc_owner"
                     class="link"
                     target="_blank"
                     :href="`${frontEndUrlToUse}ftie/ftie-page/${ticket.ftie_id}`"
                     :class="ticket.unread_ftie_id != null ? 'text-danger' : ''">{{ ticket.ftie_id }}</a>
                  <span v-else>{{ ticket.ftie_id }}</span>
                </b-col>
                <b-col class="col-2"><strong>Status:</strong> {{ ticket.status }}</b-col>
                <b-col class="col-2"><strong>Owner:</strong> {{ ticket.gtc_owner }}</b-col>
                <b-col class="col-3"><strong>Escalated:</strong> {{ ticket.escalated_date }}</b-col>
                <b-col class="col-auto" v-if="ticket.status !== 'Close' && login_user_name === data.item.gtc_owner">
                  <button type="button" class="btn btn-sm btn-danger" @click="showCloseSubTicketModal(ticket.ftie_id)">Close</button>
                </b-col>
              </b-row>
            </div>
          </template>
        </b-table>
      </div>
      <div v-if="searchMode === 'keyword'" class="table-responsive" style="min-height: 2.5rem">
        <b-table :fields="fields" :items="searchedData"
                 small
                 thead-class="thead-light text-dark"
                 :busy="searching"
                 style="width: calc(100% - 10px)">

          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>

          <!-- Select-all checkbox in header -->
          <template #head(_select)>
            <b-form-checkbox :checked="allCurrentPageSelected"
                             :indeterminate="selectedFtieIds.length > 0 && !allCurrentPageSelected"
                             @change="toggleSelectAll">
            </b-form-checkbox>
          </template>
          <template #cell(_select)="data">
            <b-form-checkbox :value="String(data.item.ftie_id)"
                             v-model="selectedFtieIds">
            </b-form-checkbox>
          </template>

          <template #cell(ftie_id)="data">
            <gtc-ftie-page v-if="data.item!=null" :prop_ftie_page_obj="data.item" :prop_user_sites="userSites" :prop_user="login_user_name" :prop_user_roles="userRoles" />
          </template>
        </b-table>
      </div>
      <!-- Pagination -->
      <div class="d-flex align-items-center justify-content-between pr-3 mt-2">
        <b-pagination v-model="currentPage"
                      :total-rows="rows"
                      :per-page="perPage"
                      :disabled="pageDisabled"
                      :limit="pageLimit"
                      @change="pageChange"
                      class="mb-0"></b-pagination>
      </div>
    </div>

    <!-- ===== CLOSE SUB-TICKET MODAL ===== -->
    <b-modal centered
             ok-title="Close Ticket"
             @ok="closeSubTicket"
             cancel-title="Cancel"
             ok-variant="danger"
             ref="CloseSubTicketModal"
             :title="`FTIE#${closeSubTicketFTIE} — Close Sub-ticket`"
             button-size="sm">
      <div>
        <div class="sub-label mb-1">Close Reason:</div>
        <gtc-textarea v-model="closeSubTicketReason" />
      </div>
    </b-modal>

  </div>
  <div v-else class="coming-soon-page">
    <div class="coming-soon-card">

      <!-- Animated icon -->
      <div class="cs-icon-area">
        <div class="cs-icon-ring">
          <i class="fa fa-cogs cs-cog-icon"></i>
        </div>
        <div class="cs-steam-row">
          <span class="cs-steam"></span>
          <span class="cs-steam"></span>
          <span class="cs-steam"></span>
        </div>
      </div>

      <!-- Brand badge -->
      <div class="cs-brand-badge">
        <img src="https://www.getac.com/img/getac-logo.png"
             alt="Getac"
             class="cs-brand-logo"
             onerror="this.style.display='none'" />
        <span class="cs-brand-text">Getac On-Premise AI · Genova</span>
      </div>

      <!-- Heading -->
      <h1 class="cs-title">We're Cooking Something Great</h1>
      <p class="cs-subtitle">
        This feature is currently under active development and not yet available in this environment.<br />
        Our engineering team is working hard to bring you a powerful AI-enhanced experience.
      </p>

      <!-- Progress bar -->
      <div class="cs-progress-wrap">
        <div class="cs-progress-label d-flex justify-content-between mb-1">
          <span><i class="fa fa-code mr-1"></i>Development in progress</span>
          <span class="cs-progress-pct">In Dev</span>
        </div>
        <div class="cs-progress-track">
          <div class="cs-progress-bar"></div>
        </div>
      </div>

      <!-- Info chips -->
      <div class="cs-chips">
        <span class="cs-chip cs-chip-orange"><i class="fa fa-bolt mr-1"></i>AI Enhanced</span>
        <span class="cs-chip cs-chip-blue"><i class="fa fa-lock mr-1"></i>Pre-Production</span>
        <span class="cs-chip cs-chip-gray"><i class="fa fa-clock-o mr-1"></i>Launching Soon</span>
      </div>

    </div>
  </div>
</template>

<script>
  import * as moment from 'moment/moment';
  import { safeHtml } from '@/helpers/vue-safehtml';

  export default {
    name: 'ftie-ai-navigator',

    data() {
      return {
        function_name: 'FITE AI Overview',

        // UI State
        searchMode: 'hybrid',          // 'semantic' | 'keyword' | 'hybrid'
        searchByListOrPage: 'List',
        includeAIOverview: true,
        includeCopilotAgent: false,
        showAIOverview: false,
        already_first_search_flag: false,
        searching: false,
        activeTool: null,
        aiOverviewExpanded: true,

        // Search fields
        keywords: '',
        es_context: '',
        date_from: '',
        date_to: '',
        customer: '',
        // Filters
        GRO: '',
        GRO_options: [
          { value: '', text: 'ALL' },
          { value: 'GDE', text: 'GDE' },
          { value: 'Getac', text: 'Getac' },
          { value: 'GFR', text: 'GFR' },
          { value: 'GMD-AFME', text: 'GMD-AFME' },
          { value: 'GMD-AU', text: 'GMD-AU' },
          { value: 'GMD-E.ASIA', text: 'GMD-E.ASIA' },
          { value: 'GMD-IN', text: 'GMD-IN' },
          { value: 'GMD-IT', text: 'GMD-IT' },
          { value: 'GMD-LATAM', text: 'GMD-LATAM' },
          { value: 'GMD-CISCEE', text: 'GMD-CISCEE' },
          { value: 'GMD-S.ASIA', text: 'GMD-S.ASIA' },
          { value: 'GMD-W.EU', text: 'GMD-W.EU' },
          { value: 'GML', text: 'GML' },
          { value: 'GTK', text: 'GTK' },
          { value: 'GUK', text: 'GUK' },
          { value: 'GUSA', text: 'GUSA' },
          { value: 'OTHERS', text: 'OTHERS' },
        ],
        model: [],
        model_options: [],
        ftieTypeList: [],
        ftieTypeOptions: [],
        KeyWordCategoryList: ['Basic Info', 'Bios/EC', 'SN', 'Subject'],
        keyword_category: 'Basic Info',
        ticket_type: '',
        ticket_type_options: [
          { value: '3rd Party Solution', text: '3rd Party Solution' },
          { value: 'Customization Support', text: 'Customization Support' },
          { value: 'Development Documents', text: 'Development Documents' },
          { value: 'Technical Inquiry', text: 'Technical Inquiry' },
          { value: 'Technical Issue', text: 'Technical Issue' },
          { value: 'Technical Support', text: 'Technical Support' },
        ],
        ticket_status: [
          { value: 'New', text: 'New' },
          { value: 'Open', text: 'Open' },
          { value: 'Solved', text: 'Solved' },
        ],
        ticket_status_options: [
          { value: 'New', text: 'New' },
          { value: 'Open', text: 'Open' },
          { value: 'Solved', text: 'Solved' },
          { value: 'Close', text: 'Closed' },
          { value: 'Deferred', text: 'Deferred' },
        ],
        NRE_status: [
          { value: 'Waiting', text: 'Ongoing' },
          { value: 'W', text: 'Awaiting' },
        ],
        NRE_status_options: [
          { value: 'Waiting', text: 'Ongoing' },
          { value: 'W', text: 'Awaiting' },
          { value: 'Completed', text: 'Completed' },
          { value: 'Rejected', text: 'Rejected' },
          { value: 'Deferred', text: 'Deferred' },
        ],

        // Results & Pagination
        searchedData: [],
        totalCount: 0,
        loadingCnt: 0,
        aiToolsComingSoon: true,
        currentPage: 1,
        perPage: 10,
        pageDisabled: false,
        pageLimit: 10,

        // AI Outputs
        similarityResult: null,
        semanticPreviewCount: 3,
        semanticShowAll: false,
        serviceRequestPreviewLength: 20,
        expandedServiceRequests: [], // 已展開的 ftie_id
        showAIHighlight: false,
        copilotStudioAgentResponse: null,
        paperOutput: '',
        paperOutputAgent: '',
        printerForOutputText: '',
        printerForOutputTextAgent: '',
        paper: '',
        paperAgent: '',

        // Advanced AI Tools
        aiDraftProposal: '',
        ai_proposal_loading: false,
        nreCostInsight: '',
        nre_loading: false,
        tcoAnalysis: '',
        tco_loading: false,
        partsShipmentInsight: '',
        parts_loading: false,

        // Sub-ticket modal
        closeSubTicketFTIE: null,
        closeSubTicketReason: '',

        // User context
        userSites: [],
        login_user_name: '',
        frontEndUrlToUse: this.$session ? this.$session.get('frontEndUrlToUse') : '',
        searchByFtieId: null,
        GRO_owner: 'ALL',
        GTC_owner: 'ALL',
        GTCOwnerList: [{ value: 'ALL', text: 'ALL' },],
        GROOwnerList: [{ value: 'ALL', text: 'ALL' },],
        decomposition: null,
        aiHighlight: null,
        aiHighlightMock: false,        // 串接後端後改為 false
        aiHighlightLoaded: false,     // 是否已產生過
        aiHighlightLoading: false,    // loading 動畫
        aiHighlightText: '',          // 顯示用純文字（call 後填入）
        showKeywordAIHighlightCard: false, // keyword mode: controls AI Highlight card visibility
        selectedFtieIds: [],              // ftie_ids user has checked; empty = use all searchedData
        selectionMode: false,             // lazy-reveal: checkboxes appear only after clicking AI Highlight
        maxHighlightTickets: 10,           // LLM context-window guard: cap tickets sent to AI Highlight
        // 預先放一份 mock，後端回傳時直接覆寫此字串即可
        aiHighlightMockText:
          '[Situation]\n' +
          'These tickets cluster around customized rugged GCS requests for UX10-series products, with customers asking to reuse an existing baseline design while adding specific interface/power/radio features.\n\n' +
          '[Key Insight]\n' +
          '- Strong repeat pattern of baseline reuse + limited customization to accelerate delivery and reduce NRE.\n' +
          '- Core technical asks center on rugged GCS integration details: power input/inrush handling, Ethernet/RF connectivity.\n' +
          '- Customers are typically moving from POC to potential volume, so early engineering feasibility is critical.\n\n' +
          '[Risk / Concern]\n' +
          '- What could go wrong?\n' +
          '  - Underestimating NRE or customization scope could delay quotes and stall customer commitment.\n' +
          '  - Baseline design may not fully satisfy requirements, causing rework after initial agreement.\n' +
          '- Escalation signals?\n' +
          '  - Requests for ASAP quote/NRE with lead-time pressure.\n' +
          '  - Missing electrical specs or interface definitions.\n\n' +
          '[Recommended Actions]\n' +
          '- Confirm which UX10 baseline can be reused and document the exact delta from standard design.\n' +
          '- Provide a fast-turn NRE estimate with assumptions clearly stated.\n' +
          '- Engage engineering/FAE early to validate power input and RF/Ethernet integration feasibility.\n\n' +
          '[Missing Info]\n' +
          '- Exact customization list vs. baseline reuse scope.\n' +
          '- Mechanical constraints, environmental/ruggedness requirements, and certification needs.\n' +
          '- Target delivery dates for POC and production, plus forecast confidence and commercial decision timeline.\n',
      };
    },

    computed: {
      fields() {
        // Checkbox column — always first; width fixed so it doesn't push other columns
        const selectField = {
          key: '_select', label: '', thClass: 'align-middle',
          thStyle: { width: '36px', 'text-align': 'center' }, sortable: false,
        };
        const baseFields = [
          { key: 'ftie_id', label: 'FTIE ID', thClass: 'align-middle', thStyle: { 'min-width': '6.5rem' }, sortable: true },
          { key: 'salesforce_opty_id', label: 'SF Opty', thClass: 'align-middle', sortable: true },
          { key: 'site', label: 'GRO', thClass: 'align-middle', sortable: true },
          { key: 'customer', label: 'Customer', thClass: 'align-middle', sortable: true },
          { key: 'vertical', label: 'Vertical', thClass: 'align-middle', sortable: true },
          { key: 'device', label: 'Device', thClass: 'align-middle', sortable: true },
          { key: 'type', label: 'Type', thClass: 'align-middle', sortable: true },
          { key: 'status', label: 'Status', thClass: 'align-middle', sortable: true },
          { key: 'gro_owner', label: 'GRO Owner', thClass: 'align-middle', sortable: true },
          { key: 'gtc_owner', label: 'GTC Owner', thClass: 'align-middle', sortable: true },
          { key: 'escalated_date', label: 'Escalate Date', thClass: 'align-middle', sortable: true },
          { key: 'close_date', label: 'Closed Date', thClass: 'align-middle', sortable: true },
          { key: 'nre_exist', label: 'Action', thClass: 'align-middle', sortable: false },
        ]

        if (this.includeAIOverview && this.es_context && this.decomposition && this.decomposition.search_mode !== 'keyword_only') {
          return [
            ...(this.selectionMode ? [selectField] : []),
            { key: 'similarity_score', label: 'AI Semantic Match', thClass: 'align-middle', sortable: true, thStyle: { width: '105px' } },
            ...baseFields
          ]
        }
        if (this.searchMode === 'keyword') {
          return [
            ...(this.selectionMode ? [selectField] : []),
            { key: 'ftie_id', label: 'FTIE ID', thClass: 'align-middle', thStyle: { display: 'None' }, sortable: false },
          ];
        }
        return [
          ...(this.selectionMode ? [selectField] : []),
          ...baseFields
        ]
      },
      rows() {
        return this.totalCount;
      },
      ticket_type_options_with_all() {
        return [{ value: '', text: 'ALL Types' }, ...this.ticket_type_options];
      },
      semanticPlaceholder() {
        return 'e.g. "Customer needs Windows Hello facial recognition on MX50 for police vehicle deployment in EU region, current IR camera module compatibility issue with BIOS 1.05..."';
      },
      date_from_computed() {
        const str = moment(this.date_from, 'YYYY-MM-DD').format('YYYY-MM-DD');
        return str === 'Invalid date' ? '' : str;
      },
      date_to_computed() {
        const str = moment(this.date_to, 'YYYY-MM-DD').format('YYYY-MM-DD');
        return str === 'Invalid date' ? '' : str;
      },
      parsedResults() {
        try {
          return JSON.parse(this.similarityResult) || [];
        } catch (e) { return []; }
      },
      // Controls the AI Overview card visibility for ALL search modes.
      // Path 1 – semantic / hybrid-with-context: showAIOverview=true after callSimilarityService.
      // Path 2 – hybrid-without-context: user manually triggers AI Highlight via the banner.
      // Keyword (Page View) tab: never shows AI Overview — the tab is legacy-only.
      showAIOverviewCard() {
        if (this.searchMode === 'keyword') return false;
        if (this.showAIOverview && !!(this.similarityResult || this.copilotStudioAgentResponse)) {
          return true;
        }
        return this.showKeywordAIHighlightCard && !!(this.aiHighlightLoading || this.aiHighlightLoaded);
      },
      // Plan C: feed selected tickets to AI when any are checked; fall back to all results.
      highlightTargets() {
        if (this.selectedFtieIds.length > 0) {
          return (this.searchedData || []).filter(
            item => this.selectedFtieIds.includes(String(item.ftie_id))
          );
        }
        return this.searchedData || [];
      },
      // True when every item in the current page is checked — drives the header checkbox.
      allCurrentPageSelected() {
        if (!this.searchedData || this.searchedData.length === 0) return false;
        return this.searchedData.every(
          item => this.selectedFtieIds.includes(String(item.ftie_id))
        );
      },
      visibleSemanticResults() {
        const list = this.parsedResults || [];
        return this.semanticShowAll ? list : list.slice(0, this.semanticPreviewCount);
      },
      parsedHighlightSections() {
        const text = this.aiHighlightText || '';
        if (!text.trim()) return [];
        const clean = text.replace(/^-{3,}\s*\n?/gm, '').trim();
        const getMeta = (title) => {
          const tl = title.toLowerCase();
          if (tl.includes('situation'))
            return { icon: 'fa-flag', key: 'situation' };
          if (tl.includes('insight'))
            return { icon: 'fa-lightbulb-o', key: 'insight' };
          if (tl.includes('risk') || tl.includes('concern'))
            return { icon: 'fa-exclamation-triangle', key: 'risk' };
          if (tl.includes('recommend') || tl.includes('action'))
            return { icon: 'fa-check-circle', key: 'action' };
          if (tl.includes('missing'))
            return { icon: 'fa-question-circle', key: 'missing' };
          return { icon: 'fa-info-circle', key: 'other' };
        };
        const sections = [];
        const regex = /\[([^\]]+)\]([\s\S]*?)(?=\[[^\]]+\]|$)/g;
        let match;
        while ((match = regex.exec(clean)) !== null) {
          const title = match[1].trim();
          const rawContent = match[2].trim();
          if (!title || !rawContent) continue;
          const meta = getMeta(title);
          const lines = [];
          rawContent.split('\n').forEach(function (rawLine) {
            const noTrail = rawLine.replace(/\s+$/, '');
            if (!noTrail.trim()) return;
            const leadSpaces = noTrail.length - noTrail.replace(/^\s+/, '').length;
            const t = noTrail.replace(/^\s+/, '');
            if (t.startsWith('- ')) {
              lines.push({ type: leadSpaces >= 2 ? 'sub-bullet' : 'bullet', text: t.slice(2) });
            } else {
              lines.push({ type: leadSpaces >= 2 ? 'sub-header' : 'text', text: t });
            }
          });
          if (lines.length) sections.push({ title, meta, lines });
        }
        return sections;
      },
    },
    mounted() {
      this.checkLoggedIn && this.checkLoggedIn();
      this.get_ftie_owner_list && this.get_ftie_owner_list();
      if (this.$route && this.$route.params.ftieId) {
        this.searchByFtieId = this.$route.params.ftieId;
        this.search && this.search();
      }
    },

    methods: {
      safeHtml,
      prdEnv() {
        return 'PRD' === this.$session.get('mode');
      },
      scoreClass(score) {
        if (score >= 80) return 'score-high';
        if (score >= 50) return 'score-mid';
        return 'score-low';
      },
      checkLoggedIn() {
        if (!this.$session.has('user_name')) {
          this.$router.push("/");
        }

        var user_name = this.$session.get('user_name');
        var options = this.$session.get('options');
        var user_email = this.$session.get('user_id');
        var user_functions = this.$session.get('user_functions');
        var user_roles = this.$session.get('user_roles');
        var user_site = this.$session.get('user_site');


        console.log("this.$session.get('options')");
        console.log(options);
        console.log(options.model_list);
        console.log("login_user_name:");
        console.log(user_name);
        console.log("user_email:");
        console.log(user_email);
        console.log("user_functions:");
        console.log(user_functions);
        console.log("user_roles:");
        console.log(user_roles);
        console.log("user_site:");
        console.log(user_site);


        this.login_user_name = user_name;
        //this.GTCOwnerList = options.gtc_owner_opt_list;
        this.GROOwnerList = options.gro_owner_opt_list;
        this.ownerSelectionDic = options.owner_dic_with_region_key;
        this.ftieTypeList = options.ftie_type_list;
        console.log("options.ftie_type_list:");
        console.log(options.ftie_type_list);

        console.log("options.gtc_owner_default:");
        console.log(options.gtc_owner_default);
        console.log("options.gro_owner_default:");
        console.log(options.gro_owner_default);

        this.GTC_owner = options.gtc_owner_default;
        this.GRO_owner = options.gro_owner_default;

        //console.log("GTCOwnerList:");
        //console.log(this.GTCOwnerList);
        //console.log("GROOwnerList:");
        //console.log(this.GROOwnerList);

        this.userFunctions = user_functions;
        this.userRoles = user_roles;
        this.userSites = user_site;
        var i;
        var model_option_new = new Array();
        for (i = 0; i < options.model_list.length; i++) {
          model_option_new.push({ value: options.model_list[i], text: options.model_list[i] })
        }
        this.model_options = this.model_options.concat(model_option_new);

        var ticketTypeOptionTmp = new Array();
        for (i = 0; i < options.ftie_type_list.length; i++) {
          ticketTypeOptionTmp.push({ value: options.ftie_type_list[i], text: options.ftie_type_list[i] })
        }
        this.ftieTypeOptions = this.ftieTypeOptions.concat(ticketTypeOptionTmp);
      },
      reportAccess() {
        if (!this.$session.has('user_name')) {
          this.$router.push("/");
        }
        var user_name = this.$session.get('user_name');
        var options = this.$session.get('options');
        var user_email = this.$session.get('user_id');
        var user_functions = this.$session.get('user_functions');
        var user_roles = this.$session.get('user_roles');
        var user_site = this.$session.get('user_site');
        if ((user_site && _.indexOf(user_site, 'Getac') > -1) && (
          (user_roles && _.indexOf(user_roles, 'Admin') > -1)
          || (user_functions && _.indexOf(user_functions, 'ES PM Leader') > -1)
          || (user_functions && _.indexOf(user_functions, 'Tech Leader') > -1)
          || (user_roles && _.indexOf(user_roles, 'ES_PM') > -1)
          || (user_roles && _.indexOf(user_roles, 'HQ_ES') > -1)
          || (user_roles && _.indexOf(user_roles, 'ES_PM_Leader') > -1)
        )
        ) {
          console.log("Access to report!");
          return true
        }
        return false;
      },
      get_ftie_owner_list() {
        let self = this;
        let url = self.$root.baseUrl + '/ftie/get-ftie-owners';

        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        eventHub.$emit('loading', true);
        let gtc_owner_list = [];
        let gro_owner_list = [];
        self.$axios.get(url, { params }, {
          headers: {
            "Authorization": `Token ${self.$store.state.token}`,
          }
        })
          .then((response) => {
            var res = response.data;
            //console.log(res);

            if (response.status === 200) {
              gtc_owner_list = JSON.parse(res['gtc_owner_list']);
              gro_owner_list = JSON.parse(res['gro_owner_list']);
              self.GTCOwnerList = gtc_owner_list;
              self.GTCOwnerList.unshift('ALL');

              self.OriginGroOwnerList = gro_owner_list;
              const distinctValues = _.uniq(_.map(gro_owner_list, 'member_gulid'));
              self.GROOwnerList = distinctValues;
              self.GROOwnerList.unshift('ALL');
            }
            else if (res.description == "login") {
              self.$session.destroy();
              self.$store.commit('clearToken');
              var url = self.$root.baseUrl + '/auth/logout/';
              self.$axios.get(url)
                .then((res) => {
                  console.log('logout');
                  self.$router.push("/");
                })
              window.location = self.frontEndUrlToUse + 'azure-login';
            }
            else {
              self.$message.exception('Internal Server Error!');
              console.log(res.description);
              //console.log(res['msg'])
            }
          }, (error) => {
            console.log(error);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {
            if ((self.userSites && _.indexOf(self.userSites, 'Getac') > -1)) {
              if (self.GTCOwnerList && _.indexOf(self.GTCOwnerList, self.login_user_name) > -1) {
                self.GTC_owner = self.login_user_name;
              }
              else {
                self.GTC_owner = 'ALL';
              }
            }
            self.GRO_owner = 'ALL';
            //self.search();
            eventHub.$emit('loading', false);
          });
      },

      getKpiReport() {
        let self = this;
        let url = self.$root.baseUrl + '/ftie/kpi-report/';
        console.log('####getFties######');
        var model_selected = [];
        var ticket_type_selected = [];
        var ticket_status_selected = [];
        var my_status_selected = [];
        var i;
        if (self.model.length == 0) {
          model_selected.push("")
        } else {
          for (i = 0; i < self.model.length; i++) {
            model_selected.push(self.model[i]['value'])
          }
        }

        if (self.ticket_status.length == 0) {
          ticket_status_selected.push("")
        }
        else {
          for (i = 0; i < self.ticket_status.length; i++) {
            ticket_status_selected.push(self.ticket_status[i]['value'])
          }
        }

        if (self.ticket_type.length == 0) {
          ticket_type_selected = ['']
        }
        else {
          for (i = 0; i < self.ticket_type.length; i++) {
            ticket_type_selected.push(self.ticket_type[i]['value'])
          }
        }
        let downloadTime = new Date();
        downloadTime = downloadTime.toISOString();

        let request_data = {
          model_selected: model_selected,
          ticket_type_selected: ticket_type_selected,
          ticket_status_selected: ticket_status_selected,
          /*my_status_selected: my_status_selected,*/
          record_per_page: self.perPage,
          page_index: self.currentPage,
          GRO: self.GRO,
          GRO_owner: self.GRO_owner,
          GTC_owner: self.GTC_owner,
          date_from: self.date_from_computed,
          date_to: self.date_to_computed,
          keywords: self.keywords,
          customer: self.customer,
          search_by_ftie_id: self.searchByFtieId,
          login_user_name: self.login_user_name,
          search_type: 'List'
        };

        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };

        eventHub.$emit('loading', true);
        self.$axios.post(url, request_data, { params, responseType: 'arraybuffer' })
          .then((res) => {
            if (res.status === 200) {
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a');
              link.href = url;
              let dateTimeStr = _.replace(downloadTime, '.', '');
              dateTimeStr = _.replace(dateTimeStr, ':', '');
              dateTimeStr = _.replace(dateTimeStr, '-', '');
              let file_name = 'ftie_kpi_report_' + dateTimeStr + '.xlsx';
              console.log("file_name =" + file_name);
              link.setAttribute('download', file_name);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }
            else if (res.description == "login") {
              self.$session.destroy();
              self.$store.commit('clearToken');
              var url = self.$root.baseUrl + '/auth/logout/';
              self.$axios.get(url)
                .then((res) => {
                  console.log('logout');
                  self.$router.push("/");
                })
              window.location = self.authUrl + 'Auth/SignOut?redirect_uri=' + encodeURIComponent(self.frontendUrl + 'azure-login');
            } else {
              self.$message.exception('Internal Server Error!');
              console.log(res.description);
            }
          }, (error) => {
            console.log(error);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {
            eventHub.$emit('loading', false);
          });
      },

      handleSearch() {
        if (this.searchMode === 'semantic') {
          this.callSimilarityService(this.es_context);
          if (this.includeCopilotAgent) this.callCopilotStudioAgent(this.es_context);
        } else if (this.searchMode === 'keyword') {
          this.perPage = 1;
          this.search();
        } else {
          // Hybrid: run both
          if (this.es_context !== null && this.es_context.length > 0) {
            this.callSimilarityService(this.es_context);
          } else {
            if (this.perPage == 1) {
              this.perPage = 10;
            }
            this.search();
          }
          // TO BE DEV NEXT
          if (this.includeCopilotAgent) this.callCopilotStudioAgent(this.es_context);
        }
      },
      search() {
        if (this.loadingCnt == 0) {
          this.already_first_search_flag = false;
          this.loadingCnt = this.loadingCnt + 1;
        }
        // this.currentPage = 1;
        this.getFties();
      },

      toggleTool(toolName) {
        this.activeTool = this.activeTool === toolName ? null : toolName;
      },

      statusVariant(status) {
        const map = {
          'Open': 'primary',
          'New': 'info',
          'Solved': 'success',
          'Close': 'secondary',
          'Deferred': 'warning',
        };
        return map[status] || 'light';
      },

      callCopilotStudioAgent(content) {
        const self = this;
        self.searching = true;
        const url = self.$root.baseUrl + '/ai-service/copilot-studio-agent/';
        const model_selected = (self.model || []).map(m => m.value);
        const request_data = {
          ftie_id: '',
          context: content,
          sfdc_request_type: '',
          es_description: content,
          es_issue_req: content,
          device: model_selected,
          site: self.GRO,
          vertical: '',
        };
        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        eventHub.$emit('loading', true);
        self.$axios.post(url, request_data, { params })
          .then((response) => {
            if (response.status === 200) {
              self.copilotStudioAgentResponse = response.data['agent_result'];
              self.printerForOutputTextAgent = self.copilotStudioAgentResponse;
              self.showAIOverview = true;
            }
          })
          .catch(err => {
            console.error(err);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {
            self.searching = false;
            self.already_first_search_flag = true;
            eventHub.$emit('loading', false);
          });
      },

      callSimilarityService(content) {
        const self = this;
        self.searching = true;
        self.showAIHighlight = false;
        self.aiHighlightLoaded = false;
        self.aiHighlightLoading = false;
        self.aiHighlightText = '';
        self.aiHighlightMock = true;
        self.selectedFtieIds = [];   // clear checkbox selection on new hybrid search
        self.selectionMode = false;  // exit selection mode on new search
        const url = self.$root.baseUrl + '/ai-service/hybrid-search/ftie/';
        const model_selected = (self.model || []).map(m => m.value);
        var ticket_type_selected = [];
        var ticket_status_selected = [];
        var my_status_selected = [];
        var i;

        if (self.ticket_status.length == 0) {
          ticket_status_selected.push("")
        }
        else {
          for (i = 0; i < self.ticket_status.length; i++) {
            ticket_status_selected.push(self.ticket_status[i]['value'])
          }
        }

        if (self.ticket_type.length == 0) {
          ticket_type_selected = ['']
        }
        else {
          for (i = 0; i < self.ticket_type.length; i++) {
            ticket_type_selected.push(self.ticket_type[i]['value'])
          }
        }


        const request_data = {
          ftie_id: '',
          context: content,
          sfdc_request_type: '',
          es_description: content,
          es_issue_req: content,
          device: model_selected,
          site: self.GRO,
          vertical: '',
          record_per_page: self.perPage,
          page_index: self.currentPage,
          ticket_type_selected: ticket_type_selected,
          ticket_status_selected: ticket_status_selected,
          GRO_owner: self.GRO_owner,
          GTC_owner: self.GTC_owner,
          date_from: self.date_from_computed,
          date_to: self.date_to_computed,
          keywords: self.keywords,
          customer: self.customer,
        };
        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        eventHub.$emit('loading', true);
        self.$axios.post(url, request_data, { params })
          .then((response) => {
            if (response.status === 200) {
              self.similarityResult = response.data['similarity_response'];
              self.searchedData = JSON.parse(response.data['ftie_info_list']);
              self.totalCount = parseInt(response.data['total_count']);
              self.printerForOutputText = self.similarityResult;
              self.showAIOverview = true;
              self.decomposition = response.data['decomposition'] || null;
            }
          })
          .catch(err => {
            // console.error(err);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {
            self.searching = false;
            self.already_first_search_flag = true;
            eventHub.$emit('loading', false);
            // self.callAISummary(self.searchedData, content)
          });
      },

      truncateText(text, limit) {
        if (!text) return '';
        return text.length > limit ? text.slice(0, limit) + '…' : text;
      },
      isServiceRequestExpanded(id) {
        return this.expandedServiceRequests.indexOf(id) !== -1;
      },
      toggleServiceRequest(id) {
        const idx = this.expandedServiceRequests.indexOf(id);
        if (idx === -1) this.expandedServiceRequests.push(id);
        else this.expandedServiceRequests.splice(idx, 1);
      },

      callAISummary(ftie_info_list, top_matched_summary, content) {
        const self = this;
        // self.aiSummarizeLoading = true;
        self.aiHighlightLoading = true;
        // TODO!! ai-ticket-overview/
        const url = self.$root.baseUrl + '/ai-service/ai-ticket-overview/';
        const request_data = {
          ftie_info_list: ftie_info_list,
          context: content,
          top_matched_summary: top_matched_summary
        };

        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        // eventHub.$emit('aiHighlightLoading', true);
        self.$axios.post(url, request_data, { params })
          .then((response) => {
            if (response.status === 200) {
              self.aiHighlightText = response.data['ai_summary'];
              self.aiHighlightMock = false;
              self.aiHighlightLoaded = true;
            }

            else {
              self.aiHighlightLoaded = false;
            }
          })
          .catch(err => {
            console.error(err);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {

            self.aiHighlightLoading = false;
            // self.searching = false;
            //self.already_first_search_flag = true;
            //eventHub.$emit('aiSummarizeLoading', false);
          });
      },
      // Enter (or re-enter) lazy-reveal selection mode.
      // Previous selectedFtieIds are intentionally preserved so the user can
      // modify their prior selection instead of starting from scratch each time.
      enterSelectionMode() {
        this.selectionMode = true;
      },

      // User confirmed selection → run AI Highlight on chosen tickets (or all if none selected).
      // Works for both first-time generation and re-generation: loaded state is reset first
      // so the loading spinner appears and stale content is cleared before the new result arrives.
      confirmAIHighlight() {
        if (this.aiHighlightLoading) return;
        this.selectionMode = false;
        // Reset previous result so re-generation shows a fresh loading state
        this.aiHighlightLoaded = false;
        this.aiHighlightText = '';
        this.showAIHighlight = true;
        // Reveal the standalone AI Highlight card when the semantic AI Overview is not shown
        // (covers keyword mode AND hybrid-without-semantic)
        if (!this.showAIOverview) {
          this.showKeywordAIHighlightCard = true;
        }
        this.callAISummary(
          this.highlightTargets,
          this.similarityResult,
          this.es_context || this.keywords,
        );
      },

      // User cancelled selection mode without generating
      cancelSelectionMode() {
        this.selectionMode = false;
        this.selectedFtieIds = [];
      },

      // Toggle AI Highlight section visibility (only called when already loaded)
      toggleAIHighlight() {
        if (this.aiHighlightLoading) return;
        this.showAIHighlight = !this.showAIHighlight;
      },

      dismissAIOverviewCard() {
        // Closes the AI Overview card for all search modes
        this.showAIOverview = false;
        this.showKeywordAIHighlightCard = false;
      },

      // Header checkbox: select / deselect every item on the current page.
      // Caps the result at maxHighlightTickets to prevent LLM context overflow.
      toggleSelectAll(checked) {
        if (checked) {
          const allIds = (this.searchedData || [])
            .map(item => String(item.ftie_id))
            .filter(Boolean);
          if (allIds.length > this.maxHighlightTickets) {
            this.selectedFtieIds = allIds.slice(0, this.maxHighlightTickets);
            /*
            alert(
              `Selection limit reached: AI Highlight supports a maximum of ${this.maxHighlightTickets} tickets.\n` +
              `The first ${this.maxHighlightTickets} tickets have been selected automatically; the rest were excluded.`
            ); */
              this.$bvModal.msgBoxOk(
                `AI Highlight supports a maximum of ${this.maxHighlightTickets} tickets ` +
                `to stay within the AI model's token context window. ` +
                `Excess selections have been removed automatically — ` +
                `please review your selection before clicking Generate.`,
                {
                  title: 'Selection Limit Reached',
                  titleTag: 'h6',
                  okTitle: 'Got it',
                  okVariant: 'warning',
                  size: 'sm',
                  centered: true,
                  headerClass: 'py-2',
                  bodyClass: 'py-3',
                }

            );
          } else {
            this.selectedFtieIds = allIds;
          }
        } else {
          this.selectedFtieIds = [];
        }
      },

      callAIDraftProposal(content) {
        const self = this;
        self.ai_proposal_loading = true;
        const url = self.$root.baseUrl + '/ai-service/draft-proposal/';
        const model_selected = (self.model || []).map(m => m.value);
        const request_data = { context: content, device: model_selected, site: self.GRO };
        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        self.$axios.post(url, request_data, { params })
          .then(response => {
            if (response.status === 200) {
              self.aiDraftProposal = response.data['proposal'] || '';
            }
          })
          .catch(err => console.error(err))
          .finally(() => { self.ai_proposal_loading = false; });
      },

      callNRECostInsight(content) {
        const self = this;
        self.nre_loading = true;
        const url = self.$root.baseUrl + '/ai-service/nre-cost-insight/';
        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        self.$axios.post(url, { context: content }, { params })
          .then(response => {
            if (response.status === 200) self.nreCostInsight = response.data['insight'] || '';
          })
          .catch(err => console.error(err))
          .finally(() => { self.nre_loading = false; });
      },

      callTCOAnalysis(content) {
        const self = this;
        self.tco_loading = true;
        const url = self.$root.baseUrl + '/ai-service/tco-tvo-analysis/';
        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        self.$axios.post(url, { context: content }, { params })
          .then(response => {
            if (response.status === 200) self.tcoAnalysis = response.data['analysis'] || '';
          })
          .catch(err => console.error(err))
          .finally(() => { self.tco_loading = false; });
      },

      callPartsShipmentInsight(content) {
        const self = this;
        self.parts_loading = true;
        const url = self.$root.baseUrl + '/ai-service/parts-shipment-insight/';
        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        self.$axios.post(url, { context: content }, { params })
          .then(response => {
            if (response.status === 200) self.partsShipmentInsight = response.data['insight'] || '';
          })
          .catch(err => console.error(err))
          .finally(() => { self.parts_loading = false; });
      },

      resetFilterCondition() {
        this.keywords = '';
        this.model = [];
        this.GRO = '';
        this.date_from = '';
        this.date_to = '';
        this.ticket_type = '';
        this.ticket_status = [
          { value: 'New', text: 'New' },
          { value: 'Open', text: 'Open' },
          { value: 'Solved', text: 'Solved' },
        ];
        this.NRE_status = [
          { value: 'Waiting', text: 'Ongoing' },
          { value: 'W', text: 'Awaiting' },
        ];
        this.searchByFtieId = null;
      },
      getFties() {
        let self = this;
        let url = self.$root.baseUrl + '/ftie/list/';
        console.log('####getFties######');
        var model_selected = [];
        var ticket_type_selected = [];
        var ticket_status_selected = [];
        var my_status_selected = [];
        var i;
        if (self.model.length == 0) {
          model_selected.push("")
        } else {
          for (i = 0; i < self.model.length; i++) {
            model_selected.push(self.model[i]['value'])
          }
        }

        if (self.ticket_status.length == 0) {
          ticket_status_selected.push("")
        }
        else {
          for (i = 0; i < self.ticket_status.length; i++) {
            ticket_status_selected.push(self.ticket_status[i]['value'])
          }
        }

        if (self.ticket_type.length == 0) {
          ticket_type_selected = ['']
        }
        else {
          for (i = 0; i < self.ticket_type.length; i++) {
            ticket_type_selected.push(self.ticket_type[i]['value'])
          }
        }

        let request_data = {
          model_selected: model_selected,
          ticket_type_selected: ticket_type_selected,
          ticket_status_selected: ticket_status_selected,
          /*my_status_selected: my_status_selected,*/
          record_per_page: self.perPage,
          page_index: self.currentPage,
          GRO: self.GRO,
          GRO_owner: self.GRO_owner,
          GTC_owner: self.GTC_owner,
          date_from: self.date_from_computed,
          date_to: self.date_to_computed,
          keywords: self.keywords,
          customer: self.customer,
          search_by_ftie_id: self.searchByFtieId,
          login_user_name: self.login_user_name,
          search_type: self.searchByListOrPage,
          keyword_category: self.keyword_category
        };

        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };

        self.searching = true;
        let searchedData = [];
        //let nreTotalCount = 2;
        let totalCount = 0;

        if (self.already_first_search_flag == false) {
          eventHub.$emit('loading', true);
        }
        self.$axios.post(url, request_data, { params })
          .then((response) => {
            var res = response.data;
            //console.log(res);

            if (response.status === 200) {
              searchedData = JSON.parse(res['ftie_list']); //ftie_info_list
              totalCount = parseInt(searchedData[0].total_count);
              //console.log(searchedData);
            }

            else if (response.status === 204) {
              self.$message.warning('No data found! Please try other selection or click reset to search all.', self.function_name);
              console.log(res.description);
            }

            else if (res.description == "login") {
              self.$session.destroy();
              self.$store.commit('clearToken');
              var url = self.$root.baseUrl + '/auth/logout/';
              self.$axios.get(url)
                .then((res) => {
                  console.log('logout');
                  self.$router.push("/");
                })
              window.location = self.frontEndUrlToUse + 'azure-login';
            } else {
              self.$message.exception('Internal Server Error!');
              console.log(res.description);
              //console.log(res['msg'])
            }
          }, (error) => {
            console.log(error);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {
            self.searching = false;
            self.already_first_search_flag = true;
            if (self.already_first_search_flag == true) {
              eventHub.$emit('loading', false);
            }
            self.searchedData = searchedData;
            self.totalCount = totalCount;
            // Reset AI Highlight state on every new keyword search so the
            // button re-arms and stale results are not shown.
            self.showKeywordAIHighlightCard = false;
            self.showAIHighlight = false;
            self.aiHighlightLoaded = false;
            self.aiHighlightLoading = false;
            self.aiHighlightText = '';
            self.selectedFtieIds = [];   // clear checkbox selection on new search
            self.selectionMode = false;  // exit selection mode on new search
          })
      },
      pageChange(page) {
        this.currentPage = page;
        this.handleSearch();
      },

      perPageChange() {
        this.currentPage = 1;
        this.handleSearch();
      },

      redirectToEtoDashboard(ftie_id) {
        window.open(this.frontendUrl + 'dashboard/' + encodeURIComponent(ftie_id));
      },

      showCloseSubTicketModal(ftie_id) {
        this.closeSubTicketFTIE = ftie_id;
        this.closeSubTicketReason = '';
        this.$refs['CloseSubTicketModal'].show();
      },

      closeSubTicket(bvModalEvt) {
        let self = this;
        bvModalEvt.preventDefault();
        let url = self.$root.baseUrl + '/ftie/close-sub-ticket/';

        let request_data = {
          ftie_id: self.closeSubTicketFTIE,
          close_reason: self.closeSubTicketReason
        };
        const params = {
          token: encodeURI(self.$store.state.token),
          username: encodeURI(self.$store.state.username),
        };
        eventHub.$emit('loading', true);
        let save_success = false;
        self.$axios.post(url, request_data, { params })
          .then((response) => {
            var res = response.data;
            //console.log(res);

            if (response.status === 200) {
              save_success = JSON.parse(res['save_success']);
            }
            else if (res.description == "login") {
              self.$session.destroy();
              self.$store.commit('clearToken');
              var url = self.$root.baseUrl + '/auth/logout/';
              self.$axios.get(url)
                .then((res) => {
                  console.log('logout');
                  self.$router.push("/");
                })
              window.location = self.frontEndUrlToUse + 'azure-login';
            } else {
              self.$message.exception('Internal Server Error!');
              console.log(res.description);
              //console.log(res['msg'])
            }
          }, (error) => {
            console.log(error);
            self.$message.exception('Internal Server Error!');
          })
          .finally(() => {
            eventHub.$emit('loading', false);
            if (save_success) {
              self.getFties()
            }
          })
        self.$nextTick(() => {
          self.$refs['CloseSubTicketModal'].hide();
        })
      },
      unreadRowClass(item, type) {
        if (item && type === 'row') {
          const unread_sub_tickets = (item.sub_tickets || []).filter(t => t.unread_ftie_id != null);
          if (item.unread_flag || unread_sub_tickets.length > 0) {
            return 'table-row-highlight table-row-bold';
          }
        }
        return null;
      },

      ifShowOptyLink(site) {
        return this.userSites.includes(site) || this.userSites.includes('Getac');
      },
      searchModeChange(target) {
        let self = this;
        if (target !== self.searchMode) {
          self.currentPage = 1;
          self.searchedData = [];
          self.searchMode = target;
          self.totalCount = 0;
          if (self.searchMode === 'keyword') {
            self.perPage = 1;
            self.searchByListOrPage = 'Page';
            // Clear all AI features — keyword/legacy tab has no AI Overview or AI Highlight
            self.showAIOverview = false;
            self.showKeywordAIHighlightCard = false;
            self.similarityResult = null;
            self.decomposition = null;
            self.aiHighlightLoaded = false;
            self.aiHighlightLoading = false;
            self.aiHighlightText = '';
            self.showAIHighlight = false;
            self.selectionMode = false;
            self.selectedFtieIds = [];
          } else if (self.perPage == 1) {
            self.perPage = 10;
            self.searchByListOrPage = 'List';
          }
        }
      }
    },

    watch: {
      // Guard against LLM context-window overflow: trim selection to maxHighlightTickets
      // whenever the array grows beyond the limit (covers individual checkboxes, select-all,
      // and any programmatic push).  Alert fires after Vue flushes the corrected state.
      selectedFtieIds(newVal) {
        if (newVal.length > this.maxHighlightTickets) {
          this.selectedFtieIds = newVal.slice(0, this.maxHighlightTickets);
          this.$nextTick(() => {
            /**
              alert(
              `Selection limit reached: AI Highlight supports a maximum of ${this.maxHighlightTickets} tickets ` +
              `to stay within the AI model's token context window.\n` +
              `Excess selections have been removed automatically. Please review your selection before clicking Generate.`
            );

 */
            this.$bvModal.msgBoxOk(
              `AI Highlight supports a maximum of ${this.maxHighlightTickets} tickets. ` +
              `The first ${this.maxHighlightTickets} tickets have been selected automatically; the rest were excluded.`,
              {
                title: 'Selection Limit Reached',
                titleTag: 'h6',
                okTitle: 'Got it',
                okVariant: 'warning',
                size: 'sm',
                centered: true,
                headerClass: 'py-2',
                bodyClass: 'py-3',
              }
            );
          });
        }
      },
    },

    filters: {
      getInputLength(val) {
        return val ? val.length : 0;
      },
    },
  };
</script>

<style scoped>

  /* ========================================
    COMING SOON PAGE
    ======================================== */
  .coming-soon-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #fdf6f0 0%, #f0f4f8 100%);
    padding: 2rem 1rem;
  }

  .coming-soon-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.10);
    padding: 3rem 2.5rem 2.5rem;
    max-width: 520px;
    width: 100%;
    text-align: center;
    border-top: 4px solid #f77530;
  }

  /* --- Icon area --- */
  .cs-icon-area {
    margin-bottom: 1.2rem;
  }

  .cs-icon-ring {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fff3ec, #ffe5d0);
    border: 2px solid rgba(247, 117, 48, 0.25);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.4rem;
  }

  .cs-cog-icon {
    font-size: 2.2rem;
    color: #f77530;
    animation: cs-spin 6s linear infinite;
  }

  @keyframes cs-spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .cs-steam-row {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .cs-steam {
    display: inline-block;
    width: 5px;
    height: 16px;
    background: #f77530;
    border-radius: 4px;
    opacity: 0;
    animation: cs-steam-rise 1.8s ease-in-out infinite;
  }

    .cs-steam:nth-child(2) {
      animation-delay: 0.35s;
    }

    .cs-steam:nth-child(3) {
      animation-delay: 0.7s;
    }

  @keyframes cs-steam-rise {
    0% {
      transform: translateY(0);
      opacity: 0;
    }

    40% {
      transform: translateY(-8px);
      opacity: 0.5;
    }

    100% {
      transform: translateY(-18px);
      opacity: 0;
    }
  }

  /* --- Brand badge --- */
  .cs-brand-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 999px;
    padding: 0.3rem 0.9rem;
    margin-bottom: 1.2rem;
  }

  .cs-brand-logo {
    height: 16px;
    object-fit: contain;
  }

  .cs-brand-text {
    font-size: 0.72rem;
    font-weight: 600;
    color: #6c757d;
    letter-spacing: 0.03em;
  }

  /* --- Text --- */
  .cs-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 0.75rem;
  }

  .cs-subtitle {
    font-size: 0.88rem;
    color: #718096;
    line-height: 1.7;
    margin-bottom: 1.6rem;
  }

  /* --- Progress bar --- */
  .cs-progress-wrap {
    margin-bottom: 1.6rem;
    text-align: left;
  }

  .cs-progress-label {
    font-size: 0.75rem;
    color: #888;
    font-weight: 600;
  }

  .cs-progress-pct {
    color: #f77530;
    font-weight: 700;
  }

  .cs-progress-track {
    height: 8px;
    background: #f0f0f0;
    border-radius: 999px;
    overflow: hidden;
  }

  .cs-progress-bar {
    height: 100%;
    width: 65%;
    background: linear-gradient(90deg, #f77530, #e05a10);
    border-radius: 999px;
    animation: cs-progress-pulse 3s ease-in-out infinite;
  }

  /*  ======================================== */


  /* ========================================
    GETAC BRAND TOKENS
    ======================================== */
  .ai-search-page {
    --getac-orange: #f77530;
    --getac-orange-light: #fff3ec;
    --getac-orange-dark: #d45e1a;
    --getac-blue: #1a3a6b;
    --getac-blue-light: #eef3fb;
    --getac-gray: #f5f6f8;
    --getac-border: #e0e3ea;
    --getac-text: #2c3142;
    --getac-text-muted: #6c757d;
    --radius-sm: 4px;
    --radius-md: 8px;
    --radius-lg: 12px;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.07);
    --shadow-md: 0 3px 12px rgba(0,0,0,0.1);
    font-family: 'Segoe UI', 'Microsoft JhengHei', Arial, sans-serif;
    color: var(--getac-text);
    padding-bottom: 2rem;
  }

  /* ========================================
    PAGE HEADER
    ======================================== */
  .page-header {
    padding: 0.75rem 0 0.5rem;
    border-bottom: 2px solid var(--getac-border);
    margin-bottom: 0.25rem;
  }

  .caption-label {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--getac-text);
    letter-spacing: 0.01em;
  }

  .header-subtitle {
    font-size: 0.75rem;
    color: var(--getac-text-muted);
    margin-top: 1px;
  }

  .header-icon-wrap {
    width: 38px;
    height: 38px;
    background: var(--getac-orange-light);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--getac-orange);
    font-size: 1.1rem;
  }

  /* ========================================
  report portal styles
  ======================================== */

  .report-portal-links {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .report-portal-label {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--getac-text-muted);
    margin-right: 0.15rem;
  }

  .report-portal-btn {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--getac-blue);
    text-decoration: none;
    padding: 1px 7px;
    border: 1px solid var(--getac-blue);
    border-radius: var(--radius-sm);
    background: var(--getac-blue-light);
    transition: background 0.15s, color 0.15s, border-color 0.15s;
    line-height: 1.7;
    white-space: nowrap;
  }

    .report-portal-btn:hover {
      background: var(--getac-blue);
      color: #fff;
      text-decoration: none;
      border-color: var(--getac-blue);
    }

  .report-portal-divider {
    font-size: 0.7rem;
    color: var(--getac-border);
    user-select: none;
  }

  .header-icon-wrap {
    width: 38px;
    height: 38px;
    background: var(--getac-orange-light);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--getac-orange);
    font-size: 1.1rem;
  }


  .ai-badge {
    font-size: 0.72rem;
    padding: 0.25em 0.5em;
  }

  .mode-badge {
    font-size: 0.72rem;
    padding: 0.25em 0.5em;
  }

  /* ========================================
    SEARCH PANEL
    ======================================== */
  .search-panel {
    background: #fff;
    border: 1px solid var(--getac-border);
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
  }

  .search-mode-tabs {
    display: flex;
    background: var(--getac-gray);
    border-bottom: 1px solid var(--getac-border);
  }

  .mode-tab {
    flex: 1;
    background: none;
    border: none;
    border-right: 1px solid var(--getac-border);
    padding: 0.6rem 1rem;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--getac-text-muted);
    text-align: left;
    transition: all 0.15s;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

    .mode-tab:last-child {
      border-right: none;
    }

    .mode-tab .tab-desc {
      font-size: 0.68rem;
      font-weight: 400;
      color: #aaa;
    }

    .mode-tab.active {
      background: #fff;
      color: var(--getac-orange);
      border-bottom: 2px solid var(--getac-orange);
      margin-bottom: -1px;
    }

      .mode-tab.active .tab-desc {
        color: #b08060;
      }

    .mode-tab:hover:not(.active) {
      background: #ececec;
      color: var(--getac-text);
    }

  .search-body {
    background: #fff;
  }

  .sub-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #555;
    margin-bottom: 0.25rem;
    display: block;
  }

  .filter-group-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--getac-blue);
    border-bottom: 1px solid var(--getac-border);
    padding-bottom: 0.3rem;
  }

  .gtc-select,
  .gtc-input {
    font-size: 0.82rem;
    height: calc(1.5em + 0.5rem + 2px);
    border-color: var(--getac-border);
  }

  .condition-filters {
    background: var(--getac-gray);
    border-radius: var(--radius-md);
    padding: 0.75rem 1rem;
    border: 1px solid var(--getac-border);
  }

  .semantic-input-area {
    background: var(--getac-blue-light);
    border-radius: var(--radius-md);
    padding: 0.75rem 1rem;
    border: 1px solid #c8d8f0;
  }

  .ai-hint-badge {
    font-size: 0.68rem;
    background: var(--getac-orange-light);
    color: var(--getac-orange-dark);
    border: 1px solid #f7c4a0;
    padding: 0.15em 0.5em;
    border-radius: 20px;
    font-weight: 600;
  }

  .semantic-textarea-wrap {
    border: 1px solid #c8d8f0;
    border-radius: var(--radius-sm);
    background: #fff;
    overflow: hidden;
  }

  .semantic-textarea {
    border: none;
    resize: vertical;
    font-size: 0.85rem;
    background: transparent;
  }

    .semantic-textarea:focus {
      box-shadow: none;
      border: none;
    }

  .textarea-footer {
    background: #f8f9fc;
    border-top: 1px solid var(--getac-border);
    font-size: 0.72rem;
  }

  .char-count {
    color: var(--getac-text-muted);
  }

  .btn-search-main {
    padding: 0.35rem 1.2rem;
    font-weight: 700;
    font-size: 0.9rem;
    border-radius: var(--radius-sm);
    background-color: var(--getac-orange);
    border-color: var(--getac-orange);
  }

    .btn-search-main:hover {
      background-color: var(--getac-orange-dark);
      border-color: var(--getac-orange-dark);
    }


  /* ========================================
    AI OVERVIEW PREREQ HINT
    ======================================== */
  .ai-overview-prereq-hint {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.68rem;
    color: #aaa;
    background: #f9f9f9;
    border: 1px dashed #ddd;
    border-radius: 20px;
    padding: 2px 10px;
    white-space: nowrap;
  }

  .hint-step-icon {
    font-size: 0.72rem;
    color: #bbb;
  }

  .hint-step-text {
    font-size: 0.68rem;
    color: #aaa;
  }

  .hint-arrow {
    font-size: 0.65rem;
    color: #ccc;
  }

  .ai-overview-lock-icon {
    font-size: 0.72rem;
    color: #ccc;
    transition: color 0.2s;
  }

    .ai-overview-lock-icon.unlocked {
      color: #52b788;
    }

  .ai-overview-label {
    font-size: 0.8rem;
    transition: color 0.2s;
  }

    .ai-overview-label.label-locked {
      color: #bbb;
    }

    .ai-overview-label.label-unlocked {
      color: #333;
      font-weight: 600;
    }

  /* ========================================
    AI OVERVIEW CARD
    ======================================== */
  .ai-overview-card {
    background: linear-gradient(135deg, #fff8f3 0%, #fef9ff 100%);
    border: 1px solid #f5c8a8;
    border-radius: var(--radius-lg);
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(247,117,48,0.08);
  }

  .ai-overview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.7rem 1rem;
    background: linear-gradient(90deg, #fff3e8, #fdf4fc);
    border-bottom: 1px solid #f0c8a0;
  }

  .ai-spark-icon {
    width: 32px;
    height: 32px;
    background: rgba(247,117,48,0.12);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
  }

  .ai-overview-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--getac-text);
  }

  .ai-overview-sub {
    font-size: 0.7rem;
    color: var(--getac-text-muted);
  }

  .source-badge {
    font-size: 0.7rem;
    border: 1px solid var(--getac-border);
    color: #555;
  }

  .ai-overview-body {
    padding: 0.85rem 1.1rem;
  }

  .overview-block {
    background: #fff;
    border: 1px solid #f0e4d8;
    border-radius: var(--radius-sm);
    padding: 0.6rem 0.85rem;
  }

  .overview-block-label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--getac-orange);
    margin-bottom: 0.4rem;
  }

  .overview-content {
    font-size: 0.85rem;
    color: #444;
    line-height: 1.65;
  }

  .ai-overview-sources {
    padding: 0.5rem 1rem;
    background: rgba(247,117,48,0.04);
    border-top: 1px solid #f0c8a0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  .source-pill-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #888;
    text-transform: uppercase;
    margin-right: 0.25rem;
  }

  .source-pill {
    font-size: 0.72rem;
    background: #fff;
    border: 1px solid #f0c8a0;
    border-radius: 20px;
    padding: 0.15em 0.65em;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: #555;
  }

    .source-pill a {
      color: var(--getac-blue);
      text-decoration: none;
      font-weight: 600;
    }

      .source-pill a:hover {
        text-decoration: underline;
      }

  .pill-score {
    background: var(--getac-orange-light);
    color: var(--getac-orange);
    border-radius: 10px;
    padding: 0 0.3em;
    font-size: 0.65rem;
    font-weight: 700;
  }

  .source-pill-more {
    background: var(--getac-gray);
    border-color: var(--getac-border);
    color: var(--getac-text-muted);
  }

  /* ========================================
    AI TOOLS PANEL
    ======================================== */
  .ai-tools-panel {
    background: #fff;
    border: 1px solid var(--getac-border);
    border-radius: var(--radius-lg);
    padding: 0.75rem 1rem;
    box-shadow: var(--shadow-sm);
  }

  .ai-tools-header {
    display: flex;
    align-items: baseline;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--getac-border);
  }

  .ai-tools-title {
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--getac-text);
  }

  .ai-tools-subtitle {
    font-size: 0.75rem;
    color: var(--getac-text-muted);
  }

  .ai-tools-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.6rem;
  }

  .ai-tool-card {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--getac-border);
    border-radius: var(--radius-md);
    background: var(--getac-gray);
    transition: all 0.15s;
  }

    .ai-tool-card.tool-active {
      border-color: var(--getac-orange);
      background: var(--getac-orange-light);
    }

    .ai-tool-card:hover {
      box-shadow: var(--shadow-sm);
    }

  .tool-icon-wrap {
    width: 34px;
    height: 34px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }

  .tool-content {
    flex: 1;
    min-width: 0;
  }

  .tool-name {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--getac-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tool-desc {
    font-size: 0.68rem;
    color: var(--getac-text-muted);
    line-height: 1.3;
    margin-top: 1px;
  }

  .tool-output-area {
    border: 1px solid var(--getac-border);
    border-radius: var(--radius-md);
    background: #fafbfd;
    padding: 0.85rem 1rem;
  }

  .tool-output-header {
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--getac-border);
    margin-bottom: 0.5rem;
  }

  .tool-textarea {
    font-size: 0.83rem;
    border-color: var(--getac-border);
    background: #fff;
  }

  .tool-empty-state {
    text-align: center;
    padding: 2rem;
    color: #bbb;
    font-size: 0.82rem;
    cursor: pointer;
    border: 2px dashed var(--getac-border);
    border-radius: var(--radius-md);
    transition: all 0.15s;
  }

    .tool-empty-state:hover {
      border-color: var(--getac-orange);
      color: var(--getac-orange);
      background: var(--getac-orange-light);
    }

  .nre-insight-display,
  .tco-display,
  .parts-display {
    font-size: 0.85rem;
    line-height: 1.65;
    color: #444;
  }

  /* ========================================
    RESULTS TABLE
    ======================================== */
  .results-header {
    padding: 0.25rem 0;
  }

  /* Selection mode banner — slides in when user clicks AI Highlight */
  .selection-mode-banner {
    background: linear-gradient(90deg, #fff8ee 0%, #fff3e0 100%);
    border: 1.5px solid #f0a04b;
    border-radius: var(--radius-md);
    box-shadow: 0 2px 8px rgba(240, 160, 75, 0.12);
  }

  ::v-deep .thead-getac th {
    background-color: var(--getac-gray) !important;
    color: var(--getac-text) !important;
    font-size: 0.78rem;
    font-weight: 700;
    border-bottom: 2px solid var(--getac-border);
    white-space: nowrap;
  }

  ::v-deep .b-table tbody tr:hover td {
    background-color: var(--getac-orange-light) !important;
  }

  .score-cell {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .score-bar-wrap {
    flex: 1;
    height: 6px;
    background: #e8e8e8;
    border-radius: 3px;
    overflow: hidden;
  }

  .score-bar {
    height: 100%;
    background: linear-gradient(90deg, var(--getac-orange), var(--getac-orange-dark));
    border-radius: 3px;
    transition: width 0.3s;
  }

  .score-label {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--getac-orange);
    white-space: nowrap;
  }

  .status-badge {
    font-size: 0.7rem;
  }

  .sub-ticket-detail {
    background: var(--getac-gray);
    border-left: 3px solid var(--getac-orange);
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }

  .link {
    color: var(--getac-blue);
    text-decoration: none;
    font-weight: 600;
  }

    .link:hover {
      color: var(--getac-orange);
      text-decoration: underline;
    }

  /* ========================================
    TYPING INDICATOR
    ======================================== */
  .typing-indicator {
    display: flex;
    align-items: center;
    padding: 0.5rem 0;
  }

    .typing-indicator span {
      width: 7px;
      height: 7px;
      background: var(--getac-orange);
      border-radius: 50%;
      display: inline-block;
      margin-right: 3px;
      animation: typing-bounce 1.2s infinite;
    }

      .typing-indicator span:nth-child(2) {
        animation-delay: 0.2s;
      }

      .typing-indicator span:nth-child(3) {
        animation-delay: 0.4s;
      }

  @keyframes typing-bounce {
    0%, 80%, 100% {
      transform: scale(0.7);
      opacity: 0.5;
    }

    40% {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* ========================================
    TRANSITIONS
    ======================================== */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity 0.25s, transform 0.25s;
  }

  .fade-slide-enter,
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }


  /* ========================================
    RESPONSIVE
    ======================================== */
  @media (max-width: 768px) {
    .search-mode-tabs {
      flex-direction: column;
    }

    .mode-tab {
      border-right: none;
      border-bottom: 1px solid var(--getac-border);
    }

    .ai-tools-grid {
      grid-template-columns: 1fr;
    }
  }

  .similarity-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
  }

  .similarity-card {
    border: 1px solid #e0e6ef;
    border-radius: 10px;
    padding: 14px 16px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    transition: box-shadow 0.2s;
  }

    .similarity-card:hover {
      box-shadow: 0 3px 10px rgba(0,0,0,0.10);
    }

  /* Header row */
  .card-header-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .card-rank {
    font-size: 0.78rem;
    color: #aaa;
    font-weight: 600;
    min-width: 24px;
  }

  .card-ftie-id {
    font-weight: 700;
    font-size: 0.95rem;
    color: #2c3e50;
    flex: 1;
  }

  .card-score {
    font-size: 0.82rem;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 12px;
  }

  /* Score color tiers */
  .score-high {
    background: #e6f9f0;
    color: #1a8a57;
  }

  .score-mid {
    background: #fff8e1;
    color: #c17f00;
  }

  .score-low {
    background: #fdecea;
    color: #c0392b;
  }

  /* Sections */
  .card-section {
    margin-top: 8px;
  }

  .card-section-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #7f8c8d;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 3px;
  }

  .card-section-body {
    font-size: 0.88rem;
    color: #34495e;
    line-height: 1.5;
  }

  .summary-text {
    font-style: italic;
    color: #555;
  }

  /* ========================================
    READ MORE (Semantic Match Summary)
    ======================================== */
  .read-more-wrap {
    text-align: center;
    margin-top: 4px;
  }

  /* Aligned with .ai-highlight-cta — subtle inline CTA, no pill border */
  .btn-read-more {
    background: transparent;
    border: none;
    padding: 2px 4px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #c17f00;
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: color 0.15s;
  }

    .btn-read-more:hover,
    .btn-read-more:focus {
      color: #f77530;
      outline: none;
      text-decoration: underline;
    }

    .btn-read-more .fa {
      font-size: 0.75rem;
      color: #bbb;
    }

    .btn-read-more:hover .fa {
      color: #f77530;
    }

  /* Inline variant — sits next to truncated body text */
  .btn-read-more-inline {
    vertical-align: baseline;
    white-space: nowrap;
  }


  /* ========================================
    AI HIGHLIGHT BLOCK
    ======================================== */
  .ai-highlight-block {
    border-color: #f0d6b0;
    background: linear-gradient(180deg, #fffdf7 0%, #fffaf2 100%);
  }

  .ai-highlight-label {
    color: #c17f00 !important;
  }

  .ai-highlight-sub {
    font-size: 0.68rem;
    font-weight: 400;
    color: #b0a190;
    margin-left: 6px;
    text-transform: none;
    letter-spacing: 0;
  }

  .ai-highlight-mock-badge {
    font-size: 0.6rem !important;
    font-weight: 600 !important;
    color: #888 !important;
    border: 1px dashed #ccc;
    background: #fafafa !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
  }

  .ai-highlight-cta {
    font-size: 0.68rem;
    color: #c17f00;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
  }

  .ai-highlight-cards {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 8px;
  }

  .ai-highlight-card {
    border: 1px solid #f0e0c0;
    background: #fff;
    border-left: 3px solid #f0a04b;
    border-radius: 8px;
    padding: 10px 14px;
    box-shadow: 0 1px 3px rgba(240, 160, 75, 0.06);
  }

  .highlight-header-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 4px;
  }

  .highlight-rank {
    font-size: 0.72rem;
    color: #b0a190;
    font-weight: 700;
  }

  .highlight-ftie {
    font-size: 0.8rem;
    font-weight: 700;
    color: #1a3a6b;
    text-decoration: none;
  }

    .highlight-ftie:hover {
      color: #f77530;
      text-decoration: underline;
    }

  .highlight-title {
    flex: 1;
    font-size: 0.88rem;
    font-weight: 600;
    color: #2c3142;
    min-width: 200px;
  }

  .highlight-status {
    font-size: 0.68rem;
  }

  .highlight-meta {
    font-size: 0.75rem;
    color: #6c757d;
    margin-bottom: 6px;
  }

  .highlight-actions-label {
    font-size: 0.7rem;
    font-weight: 700;
    color: #c17f00;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 2px;
  }

  .highlight-actions-list {
    margin: 0;
    padding-left: 1.1rem;
    font-size: 0.8rem;
    color: #4a4a4a;
    line-height: 1.55;
  }

    .highlight-actions-list li {
      margin-bottom: 2px;
    }

  .ai-highlight-text-wrap {
    margin-top: 8px;
  }

  .ai-highlight-text {
    white-space: pre-wrap;
    font-family: inherit;
    font-size: 0.85rem;
    color: #3a3a3a;
    line-height: 1.6;
    background: #fff;
    border: 1px solid #f0e0c0;
    border-left: 3px solid #f0a04b;
    border-radius: 8px;
    padding: 10px 14px;
    margin: 0;
  }

  /* ========================================
    AI HIGHLIGHT — STRUCTURED SECTIONS
    ======================================== */
  .ai-highlight-sections {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-top: 8px;
  }

  .hl-section-card {
    border-radius: 6px;
    border: 1px solid #e4e8ef;
    border-left: 3px solid #ccc;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

    .hl-section-card:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
    }

  .hl-section-header {
    display: flex;
    align-items: center;
    padding: 5px 12px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  .hl-section-title {
    letter-spacing: inherit;
  }

  .hl-section-body {
    padding: 8px 12px 6px;
    font-size: 0.83rem;
    line-height: 1.62;
    color: #3a3a3a;
  }

  /* Per-section accent — Situation */
  .hl-section-situation {
    border-left-color: var(--getac-blue);
    background: #f5f8fe;
  }

    .hl-section-situation .hl-section-header {
      background: rgba(26, 58, 107, 0.07);
      color: var(--getac-blue);
    }

  /* Per-section accent — Key Insight */
  .hl-section-insight {
    border-left-color: var(--getac-orange);
    background: #fffaf6;
  }

    .hl-section-insight .hl-section-header {
      background: rgba(247, 117, 48, 0.08);
      color: var(--getac-orange-dark);
    }

  /* Per-section accent — Risk / Concern */
  .hl-section-risk {
    border-left-color: #dc3545;
    background: #fffafa;
  }

    .hl-section-risk .hl-section-header {
      background: rgba(220, 53, 69, 0.07);
      color: #c0392b;
    }

  /* Per-section accent — Recommended Actions */
  .hl-section-action {
    border-left-color: #28a745;
    background: #f5fff7;
  }

    .hl-section-action .hl-section-header {
      background: rgba(40, 167, 69, 0.07);
      color: #1d7a3a;
    }

  /* Per-section accent — Missing Info */
  .hl-section-missing {
    border-left-color: #6c757d;
    background: #fafafa;
  }

    .hl-section-missing .hl-section-header {
      background: rgba(108, 117, 125, 0.07);
      color: #6c757d;
    }

  /* Per-section accent — fallback */
  .hl-section-other {
    border-left-color: #aaa;
    background: #fafafa;
  }

    .hl-section-other .hl-section-header {
      background: rgba(0, 0, 0, 0.04);
      color: #777;
    }

  /* Line types */
  .hl-line-text {
    margin: 0 0 4px;
    color: #3a3a3a;
  }

  .hl-line-subheader {
    margin: 6px 0 2px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #555;
  }

  .hl-line-bullet {
    position: relative;
    padding-left: 1.1rem;
    margin: 2px 0;
    color: #3a3a3a;
  }

    .hl-line-bullet::before {
      content: '•';
      position: absolute;
      left: 0.25rem;
      color: #bbb;
      font-size: 0.85em;
      line-height: inherit;
    }

  .hl-line-sub-bullet {
    position: relative;
    padding-left: 2.1rem;
    margin: 1px 0;
    font-size: 0.8rem;
    color: #555;
  }

    .hl-line-sub-bullet::before {
      content: '–';
      position: absolute;
      left: 1.35rem;
      color: #ccc;
      font-size: 0.85em;
    }
</style>



// WEBPACK FOOTER //
// src/components/framework-gen-ai/FTIEAINavigator.vue